// drawingEngine.js — Manual drawing primitives (Fase B1, Nivel 1).
//
// Scope (frozen for this phase):
//   tools = cursor | line | horizontal | vertical | rect | text | eraser | magnet
//
// Architecture:
//   • Pure / stateless module. No React. Caller (HeatmapCanvas) owns the
//     React state (`drawings`, `activeTool`, `draftRef`, `magnetOn`) and
//     calls into these helpers from pointer handlers + a render effect.
//   • Anchors are stored in CHART SPACE as {idx, price} — NEVER pixels.
//     This guarantees drawings stay anchored to the same candle/price
//     when the user pans/zooms or switches timeframe (only candles array
//     changes; idx remains the same when timeframe is preserved).
//   • Drawing IDs are short uuid-like strings so eraser can target one.
//
// Drawing shape:
//   {
//     id: 'd_xxxxxx',
//     type: 'line'|'horizontal'|'vertical'|'rect'|'text',
//     a:  {idx, price}                              // primary anchor
//     b?: {idx, price}                              // for line/rect
//     text?: string                                 // for text
//     color: '#hex'
//     width: number  (px)
//   }

import {
  xOfIdx, yOfPrice, anchorToPixel, pointerToAnchor, normalizeAnchor,
} from "./chartCoords";

// ──────────────────────────────────────────────────────────────────────
//  Constants — tweak here for visual tuning. Kept inside the module to
//  avoid scattering magic numbers in HeatmapCanvas.
// ──────────────────────────────────────────────────────────────────────
export const TOOLS = Object.freeze({
  CURSOR: "cursor",
  LINE: "line",
  HORIZONTAL: "horizontal",
  VERTICAL: "vertical",
  RECT: "rect",
  TEXT: "text",
  ERASER: "eraser",
  // 'magnet' is NOT a drawing tool — it is a modal flag that augments
  // any other tool. Kept in the same enum so the toolbar UI is uniform.
  MAGNET: "magnet",
  // ── Fase B2 — Advanced tools ──────────────────────────────────────
  // Transient measurement gauge. NOT persisted to localStorage: the
  // draft renders live during drag (ΔP, Δ%, Δi) and is dropped on
  // pointerup. Implementation: finalizeDraft() returns null so the
  // caller skips the onDrawingsChange call for this tool.
  MEASURE: "measure",
  // Fibonacci retracement (levels between 0..1) — 2 anchors, drag tool.
  FIB_RETRACEMENT: "fib_retracement",
  // Fibonacci extension (levels >1, extended past the anchor range) —
  // 2 anchors, drag tool.
  FIB_EXTENSION: "fib_extension",
  // Parallel channel — 3 anchors (a→b = trendline, c = offset point).
  // Multi-step CLICK tool: each pointerdown commits one anchor; on the
  // third click the draft is finalized into a permanent drawing.
  CHANNEL: "channel",
  // Long/Short position planner — 3 anchors (entry, target, stop).
  // Multi-step CLICK tool. Computes R:R + %gain + %loss; rendered as
  // a green profit zone + a red loss zone bracketing entry.
  POSITION: "position",
  // ── Fase B3a — Shapes & annotations ──────────────────────────────
  // Circle: 2 anchors (center + point on circumference). Radius is
  // computed at RENDER time from the pixel distance between A and B,
  // so it stays "round on screen" rather than skewed by axis units.
  CIRCLE: "circle",
  // Ellipse: 2 anchors = opposite corners of bounding box (like rect).
  ELLIPSE: "ellipse",
  // Triangle: 3 anchors (multi-step click — same flow as channel).
  TRIANGLE: "triangle",
  // Freehand brush: pointermove samples chart-space anchors at >=3px
  // pixel distance, capped at 200 points. On pointerup the path is
  // simplified via Ramer-Douglas-Peucker before being persisted.
  FREEHAND: "freehand",
  // Polyline: each pointerdown appends one vertex; double-click or
  // Enter finalizes (>=2 vertices required); Esc cancels.
  POLYLINE: "polyline",
  // Callout: 2 anchors (target arrow tip + text-box position). Prompts
  // for label text at finalize. Renders an arrow + a rounded text box.
  CALLOUT: "callout",
  // Flag/signpost: single anchor + label. NOT a separate primitive —
  // re-uses the same `text` storage shape with an extra `icon` prefix
  // so the existing text hit-test and eraser path keep working.
  FLAG: "flag",
  // ── Fase B4a — Advanced Fibonacci + Harmonic patterns + Elliott ──
  // Manual tools (user places anchors; engine renders + computes ratios).
  // NO automatic pattern detection or validation in this MVP.
  FIB_TIMEZONE: "fib_timezone",     // 1-click — vertical lines at idx + Fib(n) candles
  FIB_SPEEDFAN: "fib_speedfan",     // drag — radial fan from anchor A through 1/8…7/8 of A→B box
  FIB_WEDGE: "fib_wedge",           // drag — 2 trendlines from common origin opening at fib ratios
  XABCD: "xabcd",                   // 5 clicks — X,A,B,C,D with leg ratios
  CYPHER: "cypher",                 // 5 clicks — same render, cypher-default ratios shown
  THREE_DRIVES: "three_drives",     // 3 clicks — 2 symmetric drives + ratio
  ELLIOTT_IMPULSE: "elliott_impulse",   // 5 clicks — labels "1,2,3,4,5"
  ELLIOTT_CORRECTION: "elliott_correction", // 3 clicks — labels "A,B,C"
  // ── Fase B4b — Gann tools ────────────────────────────────────────
  // GANN FAN: 2 anchors define the 1x1 reference (Δprice / Δidx ratio).
  // Engine then draws 9 rays from anchor A with classic Gann ratios:
  //   1x8, 1x4, 1x3, 1x2, 1x1, 2x1, 3x1, 4x1, 8x1.
  // The visual angle is intentionally NOT scale-invariant: zooming the
  // price axis will visually change the apparent angle, which is the
  // mathematically expected behaviour for Gann fans.
  GANN_FAN: "gann_fan",
  // GANN BOX: 2 anchors define a rectangle (top-left/bottom-right like
  // rect). The interior is partitioned by octaves and thirds on both
  // axes (re-uses EIGHTHS_AND_THIRDS), plus the two main diagonals.
  GANN_BOX: "gann_box",
  // ── Fase B5a — Annotations, projections, measurement & manual patterns ──
  // HIGHLIGHTER: same sampling+RDP pipeline as FREEHAND but rendered as a
  // wide semi-transparent stroke (alpha ~0.30, width ~14) for "marking"
  // areas of interest on the heatmap. Stored type: 'highlighter'.
  HIGHLIGHTER: "highlighter",
  // PITCHFORK (Andrews): 3 anchors P0, P1, P2. The median line goes from
  // P0 through midpoint(P1,P2). Two parallel rails pass through P1 & P2.
  // Multi-step click flow (same as channel/triangle).
  PITCHFORK: "pitchfork",
  // FIB CHANNEL: 2 anchors define the base trendline. We offset parallel
  // lines at FIB_RETRACEMENT ratios (perpendicular distance scaled).
  // Drag tool (similar to channel but parallels are computed, not clicked).
  FIB_CHANNEL: "fib_channel",
  // FIB EXTENSION BASED ON TREND: 3 anchors A, B, C. Project ratios of
  // |A→B| from C. Multi-step click flow (same as triangle/channel).
  FIB_EXTENSION_TREND: "fib_extension_trend",
  // FORECAST: polyline-based projection allowed to extend past the last
  // candle (idx > nCandles-1 is supported by xOfIdx, lines remain visible
  // when the user pans into the future). Same flow as POLYLINE.
  FORECAST: "forecast",
  // RANGE MEASUREMENTS (persisted — distinct from the transient MEASURE
  // gauge of B2). Three flavours:
  //   PRICE_RANGE       — 2 anchors, Y-only band; labels ΔP / Δ%
  //   DATE_RANGE        — 2 anchors, X-only band; label Δi (candles)
  //   DATE_PRICE_RANGE  — 2 anchors, full rect; both labels
  PRICE_RANGE: "price_range",
  DATE_RANGE: "date_range",
  DATE_PRICE_RANGE: "date_price_range",
  // ABCD manual — 4-anchor click flow (X is omitted compared to XABCD).
  ABCD: "abcd",
  // HEAD & SHOULDERS manual — 7 click anchors:
  //   LS (left-shoulder peak), V1 (valley), H (head peak), V2 (valley),
  //   RS (right-shoulder peak), N1 (neck left), N2 (neck right).
  HEAD_SHOULDERS: "head_shoulders",
  // TRIANGLE PATTERN (chartist) — 4 anchors: 2 define upper trendline,
  // 2 define lower trendline. The two lines are NOT closed — explicitly
  // distinct from B3a's `triangle` (closed 3-anchor shape).
  TRIANGLE_PATTERN: "triangle_pattern",
  // CYCLE LINES — 2 anchors define a cycle length (Δidx). Engine then
  // draws repeating vertical lines every N idx forward up to chart edge.
  // Distinct from FIB_TIMEZONE (Fibonacci offsets); here the spacing is
  // CONSTANT and equal to the user-defined cycle.
  CYCLE_LINES: "cycle_lines",
  // ── Fase B5b — Bars pattern (capture + reposition) ───────────────
  // Two-phase tool:
  //   Phase 1 (capture):   drag-select on the heatmap to pick a range of
  //                        real candles. On pointerup we compute per-candle
  //                        OHLC RATIOS relative to the first candle's close
  //                        and store them on a TRANSIENT draft (no
  //                        persistent drawing yet). The active tool stays
  //                        BARS_PATTERN, but draft.phase is now "place".
  //   Phase 2 (placement): the next pointerdown commits a permanent
  //                        drawing with type='bars_pattern', the captured
  //                        ratios and a fresh anchor={idx, price}.
  // Stored shape:
  //   { type:'bars_pattern', span_idx:N, ratios:[{o,h,l,c},...],
  //     anchor:{idx,price}, id, color, width, opacity }
  // We deliberately store OHLC RATIOS (multipliers vs anchor.price) so
  // the candle SHAPES survive relocation — re-rendering uses the same
  // primitive as the real candle layer (`drawCandle`). No absolute prices
  // are persisted, matching the spec's "% relativos" rule.
  BARS_PATTERN: "bars_pattern",
  // ── Fase B6 — Professional gaps (5 tools) ─────────────────────────
  // RAY: 2 anchors (drag). Same gesture as LINE but the segment is
  // extended infinitely past anchor B in the P0→P1 direction. Anchor A
  // remains the start (no extension behind). Category: Líneas.
  RAY: "ray",
  // HORIZONTAL_RAY: 1 anchor (click). Horizontal line that extends ONLY
  // to the right (idx > a.idx). Distinct from HORIZONTAL (infinite both
  // directions). Category: Líneas.
  HORIZONTAL_RAY: "horizontal_ray",
  // VERTICAL_RAY: 1 anchor (click). Vertical line that extends ONLY
  // upward (price > a.price). Distinct from VERTICAL. Category: Líneas.
  VERTICAL_RAY: "vertical_ray",
  // FLAG_CHARTIST: 4-anchor click pattern.
  //   P0 = start of pole, P1 = end of pole / apex of consolidation,
  //   P2 = endpoint of upper trendline of the body,
  //   P3 = endpoint of lower trendline of the body.
  // Render = 3 segments: P0→P1 (pole), P1→P2 (upper), P1→P3 (lower).
  // Parallel P1→P2 / P1→P3 → flag; convergent → pennant/wedge.
  // DISTINCT from B3a 'flag' (textual signpost). Category: Patrones.
  FLAG_CHARTIST: "flag_chartist",
  // SCHIFF_PITCHFORK: 3-anchor click pattern (same anchors as PITCHFORK).
  // Standard Schiff median: starts at midpoint(P0,P1) → midpoint(P1,P2).
  // Rails parallel through P1 and P2. Source: TradingView docs +
  // Optuma + gocharting (2025-02). Distinct from PITCHFORK (Andrews,
  // origin at P0). Category: Líneas.
  SCHIFF_PITCHFORK: "schiff_pitchfork",
  // MODIFIED_SCHIFF_PITCHFORK: 3-anchor (same anchors as PITCHFORK).
  // Median origin = midpoint(P0, midpoint(P1,P2)) — i.e. halfway between
  // P0 and the midpoint of P1→P2. Direction: through midpoint(P1,P2).
  // Rails parallel through P1 and P2. Produces a FLATTER median than
  // standard Schiff. Source: TradingView docs + Optuma + facebook
  // chartist guides (verified 2025-02). Category: Líneas.
  MODIFIED_SCHIFF_PITCHFORK: "modified_schiff_pitchfork",
  // CUP_HANDLE: 5-anchor click pattern.
  //   LL (left lip), CB (cup bottom), RL (right lip),
  //   HB (handle bottom), HE (handle end / breakout point).
  // Render: smooth quadratic curve through LL→CB→RL for the cup,
  // then a 2-segment handle RL→HB→HE. Category: Patrones.
  CUP_HANDLE: "cup_handle",
});

// B4a constants — Fibonacci candle offsets for time-zone vertical lines.
const FIB_TIMEZONE_OFFSETS = Object.freeze([1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144]);
// Eighths/thirds used by the Speed Resistance Fan; mirrored for Gann Box (B4b).
export const EIGHTHS_AND_THIRDS = Object.freeze([1 / 8, 1 / 3, 3 / 8, 1 / 2, 5 / 8, 2 / 3, 7 / 8]);
// Gann Fan ratios: each tuple is [timeUnits, priceUnits]. The 1x1 ray
// passes through anchor B (defining the reference unit), and the other
// rays scale the time/price legs accordingly. Order kept low→high steepness.
const GANN_FAN_RATIOS = Object.freeze([
  // less-steep family (more time per unit price)
  [8, 1], [4, 1], [3, 1], [2, 1],
  // 1x1 reference
  [1, 1],
  // steeper family (more price per unit time)
  [1, 2], [1, 3], [1, 4], [1, 8],
]);
// Color map for Gann rays — 1x1 stays cyan (the reference), others
// graduate through warm→cool so the user can read the fan at a glance.
const GANN_FAN_COLORS = Object.freeze({
  "8x1": "#94a3b8", // slate-400 — flattest
  "4x1": "#a78bfa", // violet-400
  "3x1": "#60a5fa", // blue-400
  "2x1": "#34d399", // emerald-400
  "1x1": "#22d3ee", // cyan-400 — anchor
  "1x2": "#facc15", // amber-400
  "1x3": "#fb923c", // orange-400
  "1x4": "#f87171", // red-400
  "1x8": "#f43f5e", // rose-500 — steepest
});
// Fase B5a — Fib channel parallel offsets (ratios of the perpendicular
// offset from anchor C to line A→B). Reuses the retracement ratio set.
const FIB_CHANNEL_RATIOS = Object.freeze([0.236, 0.382, 0.5, 0.618, 0.786, 1.0]);
// Fase B5a — Trend-based Fibonacci extension ratios (projected from C).
const FIB_EXT_TREND_RATIOS = Object.freeze([0.618, 1.0, 1.618, 2.618]);
// Highlighter visual constants. Wide stroke + low alpha so the user can
// shade an area of interest without obscuring the heatmap underneath.
const HIGHLIGHTER_WIDTH = 14;
const HIGHLIGHTER_ALPHA = 0.30;
// Typical Cypher target ratios — purely informational labels overlaid
// on the segments. We do NOT validate these for "is it a real Cypher".
const CYPHER_RATIOS = Object.freeze({ XA_to_B: "0.382-0.618", AB_to_C: "1.13-1.414", XA_to_D: "0.786" });

// Default style palette — drawings inherit these at creation time, can
// be overridden later when we ship per-drawing style menus (future P2).
export const DEFAULT_STYLE = Object.freeze({
  color: "#22d3ee",      // cyan-400 — distinct from S/R, Patrones, Velas
  width: 1.5,
  textSize: 12,
  fillAlpha: 0.10,       // rect fill opacity
});

// ── B7b — Style picker presets ────────────────────────────────────────
// Exposed for the context menu UI so the menu and the engine stay in
// sync on what's a valid swatch / width. Strictly enumerated palettes
// keep the visual vocabulary curated (no random user-chosen colors
// breaking contrast on dark backgrounds), with a single "custom" chip
// that opens the native <input type="color"> for the rare power user.
export const STYLE_SWATCHES = Object.freeze([
  "#22d3ee", // cyan-400 (default)
  "#3b82f6", // blue-500
  "#10b981", // emerald-500
  "#facc15", // yellow-400
  "#fb923c", // orange-400
  "#ef4444", // red-500
  "#ec4899", // pink-500
  "#a855f7", // purple-500
  "#e2e8f0", // slate-200 (near-white)
  "#71717a", // zinc-500 (muted)
]);
export const STYLE_WIDTHS = Object.freeze([
  { id: "thin",   px: 1   },
  { id: "normal", px: 1.5 },
  { id: "thick",  px: 3   },
  { id: "bold",   px: 5   },
]);

// Types whose width is fixed by design (semantic visual — making them
// editable would break the affordance). Highlighter is the canonical
// case: HIGHLIGHTER_WIDTH=14 + HIGHLIGHTER_ALPHA=0.30 give it the
// "marker on paper" feel — narrower widths just turn it into a normal
// freehand. Used by the context menu to disable the Grosor submenu.
export const STYLE_WIDTH_LOCKED_TYPES = Object.freeze(["highlighter"]);

export function isStyleWidthLocked(d) {
  if (!d || !d.type) return false;
  return STYLE_WIDTH_LOCKED_TYPES.includes(d.type);
}

// Fase B2 — Fibonacci level presets. Caller can later override via the
// drawing's own `levels` array if we ship a level-customization UI.
export const DEFAULT_FIB_RETRACEMENT = Object.freeze([0, 0.236, 0.382, 0.5, 0.618, 0.786, 1.0]);
export const DEFAULT_FIB_EXTENSION   = Object.freeze([1.272, 1.618, 2.618]);
// Color palette for fib levels — matches the canonical TradingView feel
// (varies by importance). Cyan for the 50% and bookends keeps it
// consistent with the rest of the drawing layer.
const FIB_COLORS = {
  0:     "#94a3b8",  // slate-400
  0.236: "#ef4444",  // red-500
  0.382: "#f97316",  // orange-500
  0.5:   "#22d3ee",  // cyan-400 — anchor line
  0.618: "#84cc16",  // lime-500
  0.786: "#22c55e",  // green-500
  1.0:   "#94a3b8",
  1.272: "#a855f7",  // purple-500
  1.618: "#ec4899",  // pink-500
  2.618: "#fb7185",  // rose-400
};
function fibColor(level, fallback, customOverride) {
  // When the user picks a custom color via the context menu (B7b), the
  // chosen color UNIFORMLY replaces the TradingView-canonical palette
  // for fib-family drawings (fib_retracement, fib_extension, fib_speedfan,
  // fib_wedge, fib_channel, fib_extension_trend, gann_box). Toggled via
  // the per-drawing `colorIsCustom` flag set when the user clicks a
  // swatch. Drawings without the flag continue to use the multicolor
  // palette as today — zero visual regression for existing drawings.
  if (customOverride) return fallback || DEFAULT_STYLE.color;
  return FIB_COLORS[level] || fallback || DEFAULT_STYLE.color;
}

// Hit-detection tolerance for eraser / hover (in CSS pixels).
const HIT_TOL_PX = 6;
// Magnet radius — anchor snaps to a candle vertex (O/H/L/C) within this
// pixel distance. Above this distance, anchor stays where pointer dropped.
const MAGNET_RADIUS_PX = 12;

// ──────────────────────────────────────────────────────────────────────
//  ID factory — collision-resistant short tag, no external deps.
// ──────────────────────────────────────────────────────────────────────
function makeId() {
  return "d_" + Math.random().toString(36).slice(2, 8) + Date.now().toString(36).slice(-4);
}

// ──────────────────────────────────────────────────────────────────────
//  Draft lifecycle — a draft is an in-progress drawing (after pointerdown,
//  before pointerup). Stored in a useRef on the React side.
// ──────────────────────────────────────────────────────────────────────

/**
 * Start a new draft from the first anchor (pointerdown).
 * Returns `null` for tools that do not need dragging (text, horizontal,
 * vertical — those finalize on a single click).
 */
export function startDraft(tool, anchor) {
  const a = { ...anchor };
  switch (tool) {
    case TOOLS.LINE:
    case TOOLS.RECT:
    case TOOLS.MEASURE:
    case TOOLS.FIB_RETRACEMENT:
    case TOOLS.FIB_EXTENSION:
    case TOOLS.CIRCLE:
    case TOOLS.ELLIPSE:
    case TOOLS.CALLOUT:
    case TOOLS.FIB_SPEEDFAN:
    case TOOLS.FIB_WEDGE:
    case TOOLS.GANN_FAN:
    case TOOLS.GANN_BOX:
    // Fase B5a — 2-anchor drag tools
    case TOOLS.FIB_CHANNEL:
    case TOOLS.PRICE_RANGE:
    case TOOLS.DATE_RANGE:
    case TOOLS.DATE_PRICE_RANGE:
    case TOOLS.CYCLE_LINES:
    // B6 — 2-anchor drag tool
    case TOOLS.RAY:
      return { type: tool, a, b: { ...a }, ...DEFAULT_STYLE, id: makeId() };
    case TOOLS.BARS_PATTERN:
      // Phase 1: drag-select on the heatmap to choose a range of real
      // candles. The draft holds {a, b} just like a 2-anchor drag — the
      // ratios are computed at pointerup via captureBarsRatios() (caller
      // converts the draft into a "place"-phase staging object).
      return { type: tool, phase: "capture", a, b: { ...a }, ...DEFAULT_STYLE, id: makeId() };
    case TOOLS.HORIZONTAL:
    // B6 — 1-anchor click tools
    case TOOLS.HORIZONTAL_RAY:
    case TOOLS.VERTICAL_RAY:
      return { type: tool, a, ...DEFAULT_STYLE, id: makeId() };
    case TOOLS.VERTICAL:
    case TOOLS.FIB_TIMEZONE:
      return { type: tool, a, ...DEFAULT_STYLE, id: makeId() };
    case TOOLS.TEXT:
      return { type: tool, a, text: "", ...DEFAULT_STYLE, id: makeId() };
    case TOOLS.FLAG:
      // Flag re-uses the `text` storage shape but carries an `icon`
      // prefix and forces a smaller text size for the signpost look.
      return { type: "text", a, text: "", icon: "🚩", textSize: 11, ...DEFAULT_STYLE, color: "#facc15", id: makeId() };
    case TOOLS.CHANNEL:
    case TOOLS.POSITION:
    case TOOLS.TRIANGLE:
    case TOOLS.THREE_DRIVES:
    case TOOLS.ELLIOTT_CORRECTION:
    // Fase B5a — 3-anchor multi-step tools (same {a,b,c,step} schema)
    case TOOLS.PITCHFORK:
    case TOOLS.FIB_EXTENSION_TREND:
    // B6 — 3-anchor multi-step tool (Schiff variant of pitchfork)
    case TOOLS.SCHIFF_PITCHFORK:
    case TOOLS.MODIFIED_SCHIFF_PITCHFORK:
      // 3-anchor multi-step — uses {a, b, c, step} schema.
      return { type: tool, a, step: 1, ...DEFAULT_STYLE, id: makeId() };
    case TOOLS.XABCD:
    case TOOLS.CYPHER:
    case TOOLS.ELLIOTT_IMPULSE:
    // Fase B5a — multi-step points[] tools (variable finalize length)
    case TOOLS.ABCD:               // 4 clicks
    case TOOLS.TRIANGLE_PATTERN:   // 4 clicks (2 upper-line pts + 2 lower-line pts)
    case TOOLS.HEAD_SHOULDERS:     // 7 clicks (LS, V1, H, V2, RS, N1, N2)
    // B6 — 4-click flag/pennant/wedge chartist + 5-click Cup&Handle
    case TOOLS.FLAG_CHARTIST:      // 4 clicks (P0 pole-start, P1 pole-end, P2 upper, P3 lower)
    case TOOLS.CUP_HANDLE:         // 5 clicks (LL, CB, RL, HB, HE)
      // points[] multi-step — finalize length depends on tool.
      return { type: tool, points: [{ ...anchor }], step: 1, ...DEFAULT_STYLE, id: makeId() };
    case TOOLS.FREEHAND:
      return { type: tool, points: [{ ...anchor }], ...DEFAULT_STYLE, width: 2, id: makeId() };
    case TOOLS.HIGHLIGHTER:
      // Same sampling pipeline as FREEHAND but wide+translucent at render.
      return { type: tool, points: [{ ...anchor }], ...DEFAULT_STYLE,
               color: "#fde047", width: HIGHLIGHTER_WIDTH, id: makeId() };
    case TOOLS.POLYLINE:
    case TOOLS.FORECAST:
      // Multi-step variable-length: each click pushes a vertex; finalize
      // requires dbl-click or Enter (caller detects + calls finalize).
      // FORECAST is identical to POLYLINE but anchor idx values are
      // allowed to exceed candles.length (xOfIdx extrapolates linearly,
      // so the projection remains visible when the user pans the chart
      // into the future).
      return { type: tool, points: [{ ...anchor }], step: 1, ...DEFAULT_STYLE, id: makeId() };
    default:
      return null;
  }
}

/**
 * Update the second anchor of a dragging draft (pointermove).
 * No-op for tools that don't drag.
 */
export function extendDraft(draft, anchor) {
  if (!draft) return draft;
  if (draft.type === "line" || draft.type === "rect"
      || draft.type === "measure"
      || draft.type === "fib_retracement"
      || draft.type === "fib_extension"
      || draft.type === "circle"
      || draft.type === "ellipse"
      || draft.type === "callout"
      || draft.type === "gann_fan"
      || draft.type === "gann_box"
      || draft.type === "fib_channel"
      || draft.type === "price_range"
      || draft.type === "date_range"
      || draft.type === "date_price_range"
      || draft.type === "cycle_lines"
      || draft.type === "ray"
      || (draft.type === "bars_pattern" && draft.phase === "capture")) {
    return { ...draft, b: { ...anchor } };
  }
  // B5b — bars_pattern place phase: between captureEnd and the placement
  // click, follow the cursor with a live preview by updating draft.anchor.
  if (draft.type === "bars_pattern" && draft.phase === "place") {
    return { ...draft, anchor: { idx: anchor.idx, price: anchor.price } };
  }
  // Multi-step draft preview during pointermove (between clicks): when
  // step=1 we preview `b`, when step=2 we preview `c`. This gives the
  // user a live wireframe between clicks for channel/position/triangle.
  if (draft.type === "channel" || draft.type === "position" || draft.type === "triangle"
      || draft.type === "three_drives" || draft.type === "elliott_correction"
      || draft.type === "pitchfork" || draft.type === "fib_extension_trend"
      || draft.type === "schiff_pitchfork"
      || draft.type === "modified_schiff_pitchfork") {
    if (draft.step === 1) return { ...draft, b: { ...anchor } };
    if (draft.step === 2) return { ...draft, c: { ...anchor } };
  }
  // 5-anchor preview: render the previous committed segments + a ghost
  // segment from the last committed point to the live cursor.
  if (draft.type === "xabcd" || draft.type === "cypher" || draft.type === "elliott_impulse"
      || draft.type === "abcd"
      || draft.type === "triangle_pattern"
      || draft.type === "head_shoulders"
      || draft.type === "flag_chartist"
      || draft.type === "cup_handle") {
    return { ...draft, previewPt: { ...anchor } };
  }
  // Polyline live preview: the current pointer position is shown as
  // the "next vertex" ghost without being committed until pointerdown.
  if (draft.type === "polyline" || draft.type === "forecast") {
    return { ...draft, previewPt: { ...anchor } };
  }
  return draft;
}

/**
 * Append a freehand sample point. Caller has already verified that the
 * new anchor is >=3 pixels away from the last point AND that the trace
 * has not yet reached MAX_FREEHAND_POINTS. We keep the cap check here
 * defensively so the engine never grows an unbounded list.
 */
const MAX_FREEHAND_POINTS = 200;
export function appendFreehandPoint(draft, anchor) {
  if (!draft || (draft.type !== "freehand" && draft.type !== "highlighter")) return draft;
  if (draft.points.length >= MAX_FREEHAND_POINTS) return draft;
  return { ...draft, points: [...draft.points, { ...anchor }] };
}

/**
 * Append a polyline vertex (committed via pointerdown).
 */
export function appendPolylineVertex(draft, anchor) {
  if (!draft || (draft.type !== "polyline" && draft.type !== "forecast")) return draft;
  return { ...draft, points: [...draft.points, { ...anchor }], step: draft.step + 1 };
}

/**
 * Finalize the draft into a permanent drawing.
 * Returns `null` if the draft is degenerate (e.g. rect with zero area
 * caused by an accidental click — drop it so the canvas stays clean).
 */
export function finalizeDraft(draft, nCandles) {
  if (!draft) return null;
  // MEASURE is a transient gauge: it never becomes a permanent drawing.
  if (draft.type === "measure") return null;
  // Freehand / Highlighter: simplify path with RDP before persisting.
  if (draft.type === "freehand" || draft.type === "highlighter") {
    const simplified = simplifyRDP(draft.points, 1.2);
    if (simplified.length < 2) return null;
    return { ...draft, points: simplified.map((p) => normalizeAnchor(p, nCandles)) };
  }
  // Polyline / Forecast: need at least 2 vertices to form a line.
  // FORECAST anchors may exceed candles.length (xOfIdx extrapolates), so
  // we do NOT clamp idx; we only ensure non-negative and price is finite.
  if (draft.type === "polyline" || draft.type === "forecast") {
    if (!draft.points || draft.points.length < 2) return null;
    const norm = draft.type === "forecast"
      ? draft.points.map((p) => ({ idx: Math.max(0, p.idx), price: Number(p.price) || 0 }))
      : draft.points.map((p) => normalizeAnchor(p, nCandles));
    const out = { ...draft, points: norm };
    delete out.previewPt;
    delete out.step;
    return out;
  }
  // 5-anchor harmonic / Elliott impulse / 4-anchor ABCD / 4-anchor triangle_pattern / 7-anchor H&S.
  if (draft.type === "xabcd" || draft.type === "cypher" || draft.type === "elliott_impulse"
      || draft.type === "abcd" || draft.type === "triangle_pattern"
      || draft.type === "head_shoulders"
      || draft.type === "flag_chartist"
      || draft.type === "cup_handle") {
    const need = draft.type === "head_shoulders" ? 7
              : (draft.type === "abcd" || draft.type === "triangle_pattern"
                  || draft.type === "flag_chartist") ? 4
              : (draft.type === "cup_handle") ? 5
              : 5;
    if (!draft.points || draft.points.length < need) return null;
    const out = { ...draft, points: draft.points.map((p) => normalizeAnchor(p, nCandles)) };
    delete out.previewPt;
    delete out.step;
    return out;
  }
  const out = { ...draft, a: normalizeAnchor(draft.a, nCandles) };
  if (draft.b) out.b = normalizeAnchor(draft.b, nCandles);
  if (draft.c) out.c = normalizeAnchor(draft.c, nCandles);
  // Degenerate detection: lines / rects / fibs / circles / ellipses /
  // callouts with both anchors identical (eraser would also kill them).
  const twoAnchorLike = ["line", "rect", "fib_retracement", "fib_extension",
                          "circle", "ellipse", "callout",
                          "gann_fan", "gann_box",
                          "fib_channel", "price_range", "date_range",
                          "date_price_range", "cycle_lines",
                          "ray"].includes(draft.type);
  if (twoAnchorLike && out.a && out.b) {
    if (out.a.idx === out.b.idx && Math.abs(out.a.price - out.b.price) < 1e-9) {
      return null;
    }
  }
  if (out.step != null) delete out.step;
  return out;
}

/**
 * Ramer-Douglas-Peucker path simplification. Used to compress freehand
 * brush samples (~150-200 raw points → ~20-40 control points) before
 * persisting. Works in chart space — distance computed in the
 * idx/price domain (close enough for the visual purpose).
 *
 * @param {Array<{idx:number,price:number}>} points
 * @param {number} epsilon  Threshold in chart-space-units. Heuristic
 *                          0.5-2.0 depending on dataset; 1.2 default.
 */
export function simplifyRDP(points, epsilon) {
  if (!Array.isArray(points) || points.length < 3) return points || [];
  const eps = epsilon != null ? epsilon : 1.2;
  // Note: idx and price are in different units (candle index vs $).
  // We normalize price by a coarse range estimate so distances are
  // comparable. This is approximate but fine for visual simplification.
  let priceMin = points[0].price, priceMax = points[0].price;
  for (const p of points) {
    if (p.price < priceMin) priceMin = p.price;
    if (p.price > priceMax) priceMax = p.price;
  }
  const priceScale = Math.max(1e-6, priceMax - priceMin) / Math.max(1, points.length);
  const dist = (p, a, b) => {
    const dx = (b.idx - a.idx);
    const dy = (b.price - a.price) / priceScale;
    const len2 = dx * dx + dy * dy || 1;
    const t = (((p.idx - a.idx) * dx) + (((p.price - a.price) / priceScale) * dy)) / len2;
    const tt = Math.max(0, Math.min(1, t));
    const cx = a.idx + tt * dx;
    const cy = a.price + tt * (b.price - a.price);
    const dpx = (p.idx - cx);
    const dpy = (p.price - cy) / priceScale;
    return Math.hypot(dpx, dpy);
  };
  // Recursive RDP. Iterative version would be lighter; recursion fine
  // for ≤200 input points (max recursion depth ~ log2(200) = 8).
  const rdp = (pts) => {
    if (pts.length < 3) return pts;
    let idx = 0, maxD = 0;
    for (let i = 1; i < pts.length - 1; i++) {
      const d = dist(pts[i], pts[0], pts[pts.length - 1]);
      if (d > maxD) { maxD = d; idx = i; }
    }
    if (maxD > eps) {
      const left = rdp(pts.slice(0, idx + 1));
      const right = rdp(pts.slice(idx));
      return left.slice(0, -1).concat(right);
    }
    return [pts[0], pts[pts.length - 1]];
  };
  return rdp(points);
}

// ──────────────────────────────────────────────────────────────────────
//  Multi-step (channel / position) — pointerdown advances the draft.
//  Returns { draft, finalized }: `finalized` is the permanent drawing
//  when the last anchor was just placed (caller pushes to drawings[]).
// ──────────────────────────────────────────────────────────────────────

export function isMultiStepTool(tool) {
  return tool === TOOLS.CHANNEL || tool === TOOLS.POSITION
      || tool === TOOLS.TRIANGLE || tool === TOOLS.POLYLINE
      || tool === TOOLS.THREE_DRIVES || tool === TOOLS.ELLIOTT_CORRECTION
      || tool === TOOLS.XABCD || tool === TOOLS.CYPHER
      || tool === TOOLS.ELLIOTT_IMPULSE
      // Fase B5a multi-step tools
      || tool === TOOLS.PITCHFORK || tool === TOOLS.FIB_EXTENSION_TREND
      || tool === TOOLS.FORECAST
      || tool === TOOLS.ABCD || tool === TOOLS.TRIANGLE_PATTERN
      || tool === TOOLS.HEAD_SHOULDERS
      // B6
      || tool === TOOLS.FLAG_CHARTIST
      || tool === TOOLS.SCHIFF_PITCHFORK
      || tool === TOOLS.MODIFIED_SCHIFF_PITCHFORK
      || tool === TOOLS.CUP_HANDLE;
}

export function advanceMultiStep(tool, draft, anchor, nCandles) {
  // Step 1 (no draft yet): start one.
  if (!draft) {
    return { draft: startDraft(tool, anchor), finalized: null };
  }
  // Polyline / Forecast: each click appends ONE vertex. Finalize via dbl-click / Enter.
  if (draft.type === "polyline" || draft.type === "forecast") {
    return { draft: appendPolylineVertex(draft, anchor), finalized: null };
  }
  // 5-anchor tools (XABCD, Cypher, Elliott Impulse) — finalize at length 5.
  if (draft.type === "xabcd" || draft.type === "cypher" || draft.type === "elliott_impulse") {
    const nextPts = [...draft.points, { ...anchor }];
    if (nextPts.length >= 5) {
      const full = { ...draft, points: nextPts, step: 5 };
      return { draft: null, finalized: finalizeDraft(full, nCandles) };
    }
    return { draft: { ...draft, points: nextPts, step: nextPts.length }, finalized: null };
  }
  // Fase B5a — variable-length points[] click tools.
  //   ABCD finalizes at 4, TRIANGLE_PATTERN at 4, HEAD_SHOULDERS at 7,
  //   FLAG_CHARTIST at 4 (B6), CUP_HANDLE at 5 (B6).
  if (draft.type === "abcd" || draft.type === "triangle_pattern" || draft.type === "head_shoulders"
      || draft.type === "flag_chartist" || draft.type === "cup_handle") {
    const need = draft.type === "head_shoulders" ? 7
              : draft.type === "cup_handle" ? 5
              : 4;
    const nextPts = [...draft.points, { ...anchor }];
    if (nextPts.length >= need) {
      const full = { ...draft, points: nextPts, step: need };
      return { draft: null, finalized: finalizeDraft(full, nCandles) };
    }
    return { draft: { ...draft, points: nextPts, step: nextPts.length }, finalized: null };
  }
  // Triangle / 3-drives / Elliott correction / Pitchfork / Fib Ext Trend / Schiff Pitchfork / Modified Schiff — {a,b,c}.
  if (draft.type === "triangle" || draft.type === "three_drives" || draft.type === "elliott_correction"
      || draft.type === "pitchfork" || draft.type === "fib_extension_trend"
      || draft.type === "schiff_pitchfork"
      || draft.type === "modified_schiff_pitchfork") {
    if (draft.step === 1) {
      return { draft: { ...draft, b: { ...anchor }, step: 2 }, finalized: null };
    }
    if (draft.step === 2) {
      const full = { ...draft, c: { ...anchor }, step: 3 };
      return { draft: null, finalized: finalizeDraft(full, nCandles) };
    }
  }
  // Step 2: commit `b`.
  if (draft.step === 1) {
    return { draft: { ...draft, b: { ...anchor }, step: 2 }, finalized: null };
  }
  // Step 3: commit `c`, then finalize.
  if (draft.step === 2) {
    const full = { ...draft, c: { ...anchor }, step: 3 };
    return { draft: null, finalized: finalizeDraft(full, nCandles) };
  }
  return { draft, finalized: null };
}

// ──────────────────────────────────────────────────────────────────────
//  Magnet (snap-to-candle) — finds the nearest candle vertex (O/H/L/C)
//  within MAGNET_RADIUS_PX. Returns the snapped anchor, or the original
//  anchor unchanged when nothing is close enough.
// ──────────────────────────────────────────────────────────────────────

/**
 * Snap an anchor to the nearest candle OHLC vertex if magnet is enabled.
 * Called from pointer handlers BEFORE startDraft / extendDraft so the
 * draft already carries the snapped value.
 *
 * @param {{idx:number, price:number}} anchor   Raw pointer anchor.
 * @param {boolean} magnetOn                    Modal flag from toolbar.
 * @param {Array}   candles                     Visible candle array.
 * @param {object}  ctx                         { viewRange, chartW, chartH, nCandles }
 * @returns {{idx:number, price:number}}        Snapped (or original) anchor.
 */
export function snapAnchor(anchor, magnetOn, candles, ctx) {
  if (!magnetOn || !candles || !candles.length) return anchor;
  const px = xOfIdx(anchor.idx, ctx.nCandles, ctx.chartW);
  const py = yOfPrice(anchor.price, ctx.viewRange, ctx.chartH);
  // Only check the candle whose horizontal slot the pointer is over (±1).
  const centerIdx = Math.max(0, Math.min(ctx.nCandles - 1, Math.round(anchor.idx)));
  let best = null;
  let bestDist = MAGNET_RADIUS_PX + 1;
  for (let i = Math.max(0, centerIdx - 1); i <= Math.min(ctx.nCandles - 1, centerIdx + 1); i++) {
    const c = candles[i];
    if (!c) continue;
    const vertices = [
      { idx: i, price: c.o },
      { idx: i, price: c.h },
      { idx: i, price: c.l },
      { idx: i, price: c.c },
    ];
    for (const v of vertices) {
      const vx = xOfIdx(v.idx, ctx.nCandles, ctx.chartW);
      const vy = yOfPrice(v.price, ctx.viewRange, ctx.chartH);
      const d = Math.hypot(vx - px, vy - py);
      if (d < bestDist) { bestDist = d; best = v; }
    }
  }
  return best || anchor;
}

// ──────────────────────────────────────────────────────────────────────
//  Hit-testing — for eraser. Returns the first drawing whose geometry
//  passes within HIT_TOL_PX of (mouseX, mouseY).
// ──────────────────────────────────────────────────────────────────────

/**
 * @param {Array} drawings
 * @param {number} mx        Mouse X in chart area.
 * @param {number} my        Mouse Y in chart area.
 * @param {object} ctx
 * @returns {object|null}    Hit drawing, or null.
 */
export function hitTest(drawings, mx, my, ctx) {
  // Walk newest → oldest so the most recently drawn item wins (matches
  // the visual stacking — newest is on top).
  for (let i = drawings.length - 1; i >= 0; i--) {
    const d = drawings[i];
    if (hitsDrawing(d, mx, my, ctx)) return d;
  }
  return null;
}

function hitsDrawing(d, mx, my, ctx) {
  switch (d.type) {
    case "line": {
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      return pointSegmentDist(mx, my, A.x, A.y, B.x, B.y) <= HIT_TOL_PX;
    }
    case "horizontal": {
      const y = yOfPrice(d.a.price, ctx.viewRange, ctx.chartH);
      return Math.abs(my - y) <= HIT_TOL_PX;
    }
    case "vertical": {
      const x = xOfIdx(d.a.idx, ctx.nCandles, ctx.chartW);
      return Math.abs(mx - x) <= HIT_TOL_PX;
    }
    case "rect": {
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      const xMin = Math.min(A.x, B.x), xMax = Math.max(A.x, B.x);
      const yMin = Math.min(A.y, B.y), yMax = Math.max(A.y, B.y);
      // Hit on the BORDER (not the filled interior) — feels more intuitive
      // for eraser; interior fills should be erased by clicking the edge.
      const onEdge =
        (Math.abs(mx - xMin) <= HIT_TOL_PX && my >= yMin - HIT_TOL_PX && my <= yMax + HIT_TOL_PX) ||
        (Math.abs(mx - xMax) <= HIT_TOL_PX && my >= yMin - HIT_TOL_PX && my <= yMax + HIT_TOL_PX) ||
        (Math.abs(my - yMin) <= HIT_TOL_PX && mx >= xMin - HIT_TOL_PX && mx <= xMax + HIT_TOL_PX) ||
        (Math.abs(my - yMax) <= HIT_TOL_PX && mx >= xMin - HIT_TOL_PX && mx <= xMax + HIT_TOL_PX);
      return onEdge;
    }
    case "text": {
      const P = anchorToPixel(d.a, ctx);
      // Bounding box ≈ text size × estimated width.
      const w = (d.text || "").length * (d.textSize || DEFAULT_STYLE.textSize) * 0.6;
      const h = (d.textSize || DEFAULT_STYLE.textSize) + 4;
      return mx >= P.x - 2 && mx <= P.x + w + 2 && my >= P.y - h && my <= P.y + 4;
    }
    case "fib_retracement":
    case "fib_extension": {
      // Hit any of the level horizontal lines.
      const levels = d.type === "fib_retracement"
        ? (d.levels || DEFAULT_FIB_RETRACEMENT)
        : (d.levels || DEFAULT_FIB_EXTENSION);
      const range = d.b.price - d.a.price;
      for (const lv of levels) {
        const lp = d.a.price + lv * range;
        const ly = yOfPrice(lp, ctx.viewRange, ctx.chartH);
        if (Math.abs(my - ly) <= HIT_TOL_PX) return true;
      }
      return false;
    }
    case "channel": {
      if (!d.b || !d.c) return false;
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      const C = anchorToPixel(d.c, ctx);
      // Hit main line OR parallel line.
      if (pointSegmentDist(mx, my, A.x, A.y, B.x, B.y) <= HIT_TOL_PX) return true;
      // Compute parallel offset.
      const lineDX = B.x - A.x, lineDY = B.y - A.y;
      const len = Math.hypot(lineDX, lineDY) || 1;
      const ux = lineDX / len, uy = lineDY / len;
      const acx = C.x - A.x, acy = C.y - A.y;
      const t = acx * ux + acy * uy;
      const footX = A.x + t * ux, footY = A.y + t * uy;
      const offX = C.x - footX, offY = C.y - footY;
      const A2x = A.x + offX, A2y = A.y + offY;
      const B2x = B.x + offX, B2y = B.y + offY;
      return pointSegmentDist(mx, my, A2x, A2y, B2x, B2y) <= HIT_TOL_PX;
    }
    case "position": {
      // Hit entry horizontal line OR the chip area.
      const yE = yOfPrice(d.a.price, ctx.viewRange, ctx.chartH);
      if (Math.abs(my - yE) <= HIT_TOL_PX) return true;
      // Also accept hits inside the chip box (top-left).
      if (mx >= 8 && mx <= 200 && my >= Math.max(4, yE - 70) && my <= yE) return true;
      return false;
    }
    case "measure":
      // Measure is transient — caller should never persist it, but be
      // defensive in case a stray draft got through.
      return false;
    case "circle": {
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      const r = Math.hypot(B.x - A.x, B.y - A.y);
      const dToCenter = Math.hypot(mx - A.x, my - A.y);
      return Math.abs(dToCenter - r) <= HIT_TOL_PX;
    }
    case "ellipse": {
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      const cx = (A.x + B.x) / 2, cy = (A.y + B.y) / 2;
      const rx = Math.abs(B.x - A.x) / 2 || 1;
      const ry = Math.abs(B.y - A.y) / 2 || 1;
      const dx = (mx - cx) / rx, dy = (my - cy) / ry;
      const dist = Math.abs(Math.hypot(dx, dy) - 1);
      return dist <= HIT_TOL_PX / Math.min(rx, ry);
    }
    case "triangle": {
      if (!d.b || !d.c) return false;
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      const C = anchorToPixel(d.c, ctx);
      return pointSegmentDist(mx, my, A.x, A.y, B.x, B.y) <= HIT_TOL_PX
          || pointSegmentDist(mx, my, B.x, B.y, C.x, C.y) <= HIT_TOL_PX
          || pointSegmentDist(mx, my, C.x, C.y, A.x, A.y) <= HIT_TOL_PX;
    }
    case "freehand":
    case "polyline": {
      if (!d.points || d.points.length < 2) return false;
      for (let i = 1; i < d.points.length; i++) {
        const P1 = anchorToPixel(d.points[i - 1], ctx);
        const P2 = anchorToPixel(d.points[i], ctx);
        if (pointSegmentDist(mx, my, P1.x, P1.y, P2.x, P2.y) <= HIT_TOL_PX) return true;
      }
      return false;
    }
    case "callout": {
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      // Connector segment OR label box.
      const sz = d.textSize || DEFAULT_STYLE.textSize;
      const boxW = ((d.text || "").length * sz * 0.6) + 12;
      const boxH = sz + 12;
      const onLine = pointSegmentDist(mx, my, A.x, A.y, B.x + boxW / 2, B.y + boxH / 2) <= HIT_TOL_PX;
      const inBox = mx >= B.x && mx <= B.x + boxW && my >= B.y && my <= B.y + boxH;
      return onLine || inBox;
    }
    case "fib_timezone": {
      // Hit any of the vertical lines at offsets from anchor idx.
      for (const off of FIB_TIMEZONE_OFFSETS) {
        const xv = xOfIdx(d.a.idx + off, ctx.nCandles, ctx.chartW);
        if (Math.abs(mx - xv) <= HIT_TOL_PX) return true;
      }
      return false;
    }
    case "fib_speedfan":
    case "fib_wedge": {
      // Approximate: hit if within HIT_TOL_PX of any of the radial rays
      // emanating from anchor A. We check the 1x1 reference segment + the
      // generated rays. Cheap & good enough for an eraser tool.
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      if (pointSegmentDist(mx, my, A.x, A.y, B.x, B.y) <= HIT_TOL_PX) return true;
      const ratios = d.type === "fib_speedfan" ? EIGHTHS_AND_THIRDS : [0.382, 0.5, 0.618, 1.272, 1.618];
      const dx = B.x - A.x, dy = B.y - A.y;
      for (const r of ratios) {
        const tx = A.x + dx;
        const ty = A.y + dy * r;
        if (pointSegmentDist(mx, my, A.x, A.y, tx, ty) <= HIT_TOL_PX) return true;
        if (d.type === "fib_speedfan") {
          const tx2 = A.x + dx * r;
          const ty2 = A.y + dy;
          if (pointSegmentDist(mx, my, A.x, A.y, tx2, ty2) <= HIT_TOL_PX) return true;
        }
      }
      return false;
    }
    case "xabcd":
    case "cypher":
    case "elliott_impulse": {
      if (!d.points || d.points.length < 2) return false;
      for (let i = 1; i < d.points.length; i++) {
        const P1 = anchorToPixel(d.points[i-1], ctx);
        const P2 = anchorToPixel(d.points[i], ctx);
        if (pointSegmentDist(mx, my, P1.x, P1.y, P2.x, P2.y) <= HIT_TOL_PX) return true;
      }
      return false;
    }
    case "three_drives":
    case "elliott_correction": {
      const pts = [d.a, d.b, d.c].filter(Boolean);
      if (pts.length < 2) return false;
      for (let i = 1; i < pts.length; i++) {
        const P1 = anchorToPixel(pts[i-1], ctx);
        const P2 = anchorToPixel(pts[i], ctx);
        if (pointSegmentDist(mx, my, P1.x, P1.y, P2.x, P2.y) <= HIT_TOL_PX) return true;
      }
      return false;
    }
    case "gann_fan": {
      // Hit any of the 9 Gann rays. We bound the ray length to a
      // reasonable visual extent (3× the A→B box) so the segment hit
      // test catches the visible portion. The 1x1 reference is the
      // direct A→B segment, the rest are scaled from there.
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      const dx = B.x - A.x, dy = B.y - A.y;
      for (const [n, m] of GANN_FAN_RATIOS) {
        // Extend the ray 3× past the reference unit so the user can
        // erase the visible part of the fan, not just the [A..unit] leg.
        const ex = A.x + 3 * n * dx;
        const ey = A.y + 3 * m * dy;
        if (pointSegmentDist(mx, my, A.x, A.y, ex, ey) <= HIT_TOL_PX) return true;
      }
      return false;
    }
    case "gann_box": {
      // Hit on the border of the box OR on any interior grid line.
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      const xMin = Math.min(A.x, B.x), xMax = Math.max(A.x, B.x);
      const yMin = Math.min(A.y, B.y), yMax = Math.max(A.y, B.y);
      const onEdge =
        (Math.abs(mx - xMin) <= HIT_TOL_PX && my >= yMin - HIT_TOL_PX && my <= yMax + HIT_TOL_PX) ||
        (Math.abs(mx - xMax) <= HIT_TOL_PX && my >= yMin - HIT_TOL_PX && my <= yMax + HIT_TOL_PX) ||
        (Math.abs(my - yMin) <= HIT_TOL_PX && mx >= xMin - HIT_TOL_PX && mx <= xMax + HIT_TOL_PX) ||
        (Math.abs(my - yMax) <= HIT_TOL_PX && mx >= xMin - HIT_TOL_PX && mx <= xMax + HIT_TOL_PX);
      if (onEdge) return true;
      // Internal vertical & horizontal subdivisions.
      for (const f of EIGHTHS_AND_THIRDS) {
        const xv = xMin + f * (xMax - xMin);
        if (Math.abs(mx - xv) <= HIT_TOL_PX && my >= yMin && my <= yMax) return true;
        const yv = yMin + f * (yMax - yMin);
        if (Math.abs(my - yv) <= HIT_TOL_PX && mx >= xMin && mx <= xMax) return true;
      }
      // Diagonals.
      if (pointSegmentDist(mx, my, xMin, yMin, xMax, yMax) <= HIT_TOL_PX) return true;
      if (pointSegmentDist(mx, my, xMin, yMax, xMax, yMin) <= HIT_TOL_PX) return true;
      return false;
    }
    // ── Fase B5a hit-tests ─────────────────────────────────────────
    case "highlighter": {
      // Same shape as freehand — segment chain.
      if (!d.points || d.points.length < 2) return false;
      const tol = Math.max(HIT_TOL_PX, (d.width || HIGHLIGHTER_WIDTH) / 2);
      for (let i = 1; i < d.points.length; i++) {
        const P1 = anchorToPixel(d.points[i - 1], ctx);
        const P2 = anchorToPixel(d.points[i], ctx);
        if (pointSegmentDist(mx, my, P1.x, P1.y, P2.x, P2.y) <= tol) return true;
      }
      return false;
    }
    case "pitchfork": {
      if (!d.b || !d.c) return false;
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      const C = anchorToPixel(d.c, ctx);
      const midX = (B.x + C.x) / 2;
      const midY = (B.y + C.y) / 2;
      // Hit median A → midpoint(B,C)
      if (pointSegmentDist(mx, my, A.x, A.y, midX, midY) <= HIT_TOL_PX) return true;
      // Rails: B + (midpoint→...) and C + (midpoint→...)
      const railDX = midX - A.x;
      const railDY = midY - A.y;
      if (pointSegmentDist(mx, my, B.x, B.y, B.x + railDX, B.y + railDY) <= HIT_TOL_PX) return true;
      if (pointSegmentDist(mx, my, C.x, C.y, C.x + railDX, C.y + railDY) <= HIT_TOL_PX) return true;
      return false;
    }
    case "fib_channel": {
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      // Base line
      if (pointSegmentDist(mx, my, A.x, A.y, B.x, B.y) <= HIT_TOL_PX) return true;
      // Parallels at each ratio: perpendicular offset = ratio × (B.y - A.y).
      // The simplest hit-test approximation reuses the channel offset
      // vector (perpendicular to A→B with magnitude = priceDelta × ratio).
      const dxL = B.x - A.x, dyL = B.y - A.y;
      const len = Math.hypot(dxL, dyL) || 1;
      const nx = -dyL / len, ny = dxL / len;
      const off = (d.b.price - d.a.price);
      // Project offset into pixel space via yOfPrice difference.
      const offYpx = anchorToPixel({ idx: d.a.idx, price: d.a.price + off }, ctx).y - A.y;
      for (const r of FIB_CHANNEL_RATIOS) {
        const ox = nx * r * offYpx;
        const oy = ny * r * offYpx;
        if (pointSegmentDist(mx, my, A.x + ox, A.y + oy, B.x + ox, B.y + oy) <= HIT_TOL_PX) return true;
      }
      return false;
    }
    case "fib_extension_trend": {
      if (!d.b || !d.c) return false;
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      const C = anchorToPixel(d.c, ctx);
      // Segment A→B and segment B→C
      if (pointSegmentDist(mx, my, A.x, A.y, B.x, B.y) <= HIT_TOL_PX) return true;
      if (pointSegmentDist(mx, my, B.x, B.y, C.x, C.y) <= HIT_TOL_PX) return true;
      // Horizontal projection lines through C at each ratio
      const range = d.b.price - d.a.price;
      for (const r of FIB_EXT_TREND_RATIOS) {
        const lvY = anchorToPixel({ idx: d.c.idx, price: d.c.price + r * range }, ctx).y;
        if (Math.abs(my - lvY) <= HIT_TOL_PX && mx >= C.x - 8) return true;
      }
      return false;
    }
    case "forecast": {
      // Same hit-test as polyline (segment chain).
      if (!d.points || d.points.length < 2) return false;
      for (let i = 1; i < d.points.length; i++) {
        const P1 = anchorToPixel(d.points[i - 1], ctx);
        const P2 = anchorToPixel(d.points[i], ctx);
        if (pointSegmentDist(mx, my, P1.x, P1.y, P2.x, P2.y) <= HIT_TOL_PX) return true;
      }
      return false;
    }
    case "price_range": {
      const yA = yOfPrice(d.a.price, ctx.viewRange, ctx.chartH);
      const yB = yOfPrice(d.b.price, ctx.viewRange, ctx.chartH);
      if (Math.abs(my - yA) <= HIT_TOL_PX) return true;
      if (Math.abs(my - yB) <= HIT_TOL_PX) return true;
      return false;
    }
    case "date_range": {
      const xA = xOfIdx(d.a.idx, ctx.nCandles, ctx.chartW);
      const xB = xOfIdx(d.b.idx, ctx.nCandles, ctx.chartW);
      if (Math.abs(mx - xA) <= HIT_TOL_PX) return true;
      if (Math.abs(mx - xB) <= HIT_TOL_PX) return true;
      return false;
    }
    case "date_price_range": {
      // Bounding-box edges (reuses rect logic).
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      const xMin = Math.min(A.x, B.x), xMax = Math.max(A.x, B.x);
      const yMin = Math.min(A.y, B.y), yMax = Math.max(A.y, B.y);
      return (Math.abs(mx - xMin) <= HIT_TOL_PX && my >= yMin - HIT_TOL_PX && my <= yMax + HIT_TOL_PX) ||
             (Math.abs(mx - xMax) <= HIT_TOL_PX && my >= yMin - HIT_TOL_PX && my <= yMax + HIT_TOL_PX) ||
             (Math.abs(my - yMin) <= HIT_TOL_PX && mx >= xMin - HIT_TOL_PX && mx <= xMax + HIT_TOL_PX) ||
             (Math.abs(my - yMax) <= HIT_TOL_PX && mx >= xMin - HIT_TOL_PX && mx <= xMax + HIT_TOL_PX);
    }
    case "abcd":
    case "triangle_pattern":
    case "head_shoulders": {
      if (!d.points || d.points.length < 2) return false;
      // triangle_pattern: hit segments [0]→[1] (upper) AND [2]→[3] (lower).
      if (d.type === "triangle_pattern") {
        if (d.points.length >= 2) {
          const P1 = anchorToPixel(d.points[0], ctx);
          const P2 = anchorToPixel(d.points[1], ctx);
          if (pointSegmentDist(mx, my, P1.x, P1.y, P2.x, P2.y) <= HIT_TOL_PX) return true;
        }
        if (d.points.length >= 4) {
          const P3 = anchorToPixel(d.points[2], ctx);
          const P4 = anchorToPixel(d.points[3], ctx);
          if (pointSegmentDist(mx, my, P3.x, P3.y, P4.x, P4.y) <= HIT_TOL_PX) return true;
        }
        return false;
      }
      // head_shoulders: LS-V1-H-V2-RS chain + neckline N1-N2 (last 2 pts).
      if (d.type === "head_shoulders") {
        const main = d.points.slice(0, 5);
        for (let i = 1; i < main.length; i++) {
          const P1 = anchorToPixel(main[i - 1], ctx);
          const P2 = anchorToPixel(main[i], ctx);
          if (pointSegmentDist(mx, my, P1.x, P1.y, P2.x, P2.y) <= HIT_TOL_PX) return true;
        }
        if (d.points.length >= 7) {
          const N1 = anchorToPixel(d.points[5], ctx);
          const N2 = anchorToPixel(d.points[6], ctx);
          if (pointSegmentDist(mx, my, N1.x, N1.y, N2.x, N2.y) <= HIT_TOL_PX) return true;
        }
        return false;
      }
      // abcd: connected chain.
      for (let i = 1; i < d.points.length; i++) {
        const P1 = anchorToPixel(d.points[i - 1], ctx);
        const P2 = anchorToPixel(d.points[i], ctx);
        if (pointSegmentDist(mx, my, P1.x, P1.y, P2.x, P2.y) <= HIT_TOL_PX) return true;
      }
      return false;
    }
    case "cycle_lines": {
      // Repeated vertical lines every (b.idx - a.idx) candles starting at a.idx.
      const stride = Math.max(1, Math.round(d.b.idx - d.a.idx));
      if (stride < 1) return false;
      let idx = d.a.idx;
      // Limit how far we extend to avoid runaway hit checks.
      const maxIters = 64;
      for (let i = 0; i < maxIters; i++) {
        const xv = xOfIdx(idx, ctx.nCandles, ctx.chartW);
        if (xv > ctx.chartW + 20) break;
        if (Math.abs(mx - xv) <= HIT_TOL_PX) return true;
        idx += stride;
      }
      return false;
    }
    case "bars_pattern": {
      // Point-in-rect on the full bounding box of the synthetic candles
      // (same logic B3b uses for 'rect' — area click, not just edge).
      const bb = barsPatternBBox(d, ctx);
      if (!bb) return false;
      return mx >= bb.x && mx <= bb.x + bb.w && my >= bb.y && my <= bb.y + bb.h;
    }
    // ── B6 — Ray / Horizontal Ray / Vertical Ray / Flag chartist / Schiff Pitchfork ──
    case "ray": {
      // The ray goes from A in the direction of B (P0→P1) and extends
      // infinitely past B. Hit-test on the segment from A out to the
      // chart edge in that direction.
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      const dx = B.x - A.x, dy = B.y - A.y;
      const len = Math.hypot(dx, dy) || 1;
      // Choose a scale factor large enough to reach the chart boundary.
      const scale = (Math.max(ctx.chartW, ctx.chartH) * 2.5) / len;
      const ex = A.x + dx * scale, ey = A.y + dy * scale;
      return pointSegmentDist(mx, my, A.x, A.y, ex, ey) <= HIT_TOL_PX;
    }
    case "horizontal_ray": {
      const A = anchorToPixel(d.a, ctx);
      // Right-only: line from A.x to chartW at y=A.y.
      if (mx < A.x - HIT_TOL_PX) return false;
      return Math.abs(my - A.y) <= HIT_TOL_PX;
    }
    case "vertical_ray": {
      const A = anchorToPixel(d.a, ctx);
      // Up-only: line from A.y to y=0 at x=A.x.
      if (my > A.y + HIT_TOL_PX) return false;
      return Math.abs(mx - A.x) <= HIT_TOL_PX;
    }
    case "flag_chartist": {
      if (!d.points || d.points.length < 2) return false;
      const P = d.points.map((p) => anchorToPixel(p, ctx));
      // Pole P0→P1
      if (P.length >= 2 && pointSegmentDist(mx, my, P[0].x, P[0].y, P[1].x, P[1].y) <= HIT_TOL_PX) return true;
      // Upper trendline P1→P2
      if (P.length >= 3 && pointSegmentDist(mx, my, P[1].x, P[1].y, P[2].x, P[2].y) <= HIT_TOL_PX) return true;
      // Lower trendline P1→P3
      if (P.length >= 4 && pointSegmentDist(mx, my, P[1].x, P[1].y, P[3].x, P[3].y) <= HIT_TOL_PX) return true;
      return false;
    }
    case "schiff_pitchfork": {
      if (!d.b || !d.c) return false;
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      const C = anchorToPixel(d.c, ctx);
      // Schiff origin = midpoint(A, B). Direction = midpoint(B, C).
      const ox = (A.x + B.x) / 2, oy = (A.y + B.y) / 2;
      const midBC_x = (B.x + C.x) / 2, midBC_y = (B.y + C.y) / 2;
      const rdx = midBC_x - ox, rdy = midBC_y - oy;
      // Median (extended 1.5x past midBC for hit area)
      if (pointSegmentDist(mx, my, ox, oy, ox + rdx * 2.5, oy + rdy * 2.5) <= HIT_TOL_PX) return true;
      // Rails through B and C parallel to median
      if (pointSegmentDist(mx, my, B.x, B.y, B.x + rdx * 2.5, B.y + rdy * 2.5) <= HIT_TOL_PX) return true;
      if (pointSegmentDist(mx, my, C.x, C.y, C.x + rdx * 2.5, C.y + rdy * 2.5) <= HIT_TOL_PX) return true;
      return false;
    }
    case "modified_schiff_pitchfork": {
      if (!d.b || !d.c) return false;
      const A = anchorToPixel(d.a, ctx);
      const B = anchorToPixel(d.b, ctx);
      const C = anchorToPixel(d.c, ctx);
      // Modified Schiff: origin = midpoint(A, midpoint(B,C)).
      const midBC = { x: (B.x + C.x) / 2, y: (B.y + C.y) / 2 };
      const ox = (A.x + midBC.x) / 2, oy = (A.y + midBC.y) / 2;
      const rdx = midBC.x - ox, rdy = midBC.y - oy;
      if (pointSegmentDist(mx, my, ox, oy, ox + rdx * 2.5, oy + rdy * 2.5) <= HIT_TOL_PX) return true;
      if (pointSegmentDist(mx, my, B.x, B.y, B.x + rdx * 2.5, B.y + rdy * 2.5) <= HIT_TOL_PX) return true;
      if (pointSegmentDist(mx, my, C.x, C.y, C.x + rdx * 2.5, C.y + rdy * 2.5) <= HIT_TOL_PX) return true;
      return false;
    }
    case "cup_handle": {
      if (!d.points || d.points.length < 2) return false;
      const P = d.points.map((p) => anchorToPixel(p, ctx));
      // Cup approximated as a polyline with N=20 samples through the
      // quadratic curve LL→CB→RL (control = CB). Adequate for hit-test.
      if (P.length >= 3) {
        const steps = 20;
        let prevX = P[0].x, prevY = P[0].y;
        for (let i = 1; i <= steps; i++) {
          const t = i / steps;
          const x = (1 - t) * (1 - t) * P[0].x + 2 * (1 - t) * t * P[1].x + t * t * P[2].x;
          const y = (1 - t) * (1 - t) * P[0].y + 2 * (1 - t) * t * P[1].y + t * t * P[2].y;
          if (pointSegmentDist(mx, my, prevX, prevY, x, y) <= HIT_TOL_PX) return true;
          prevX = x; prevY = y;
        }
      }
      // Handle segments RL→HB→HE
      if (P.length >= 4 && pointSegmentDist(mx, my, P[2].x, P[2].y, P[3].x, P[3].y) <= HIT_TOL_PX) return true;
      if (P.length >= 5 && pointSegmentDist(mx, my, P[3].x, P[3].y, P[4].x, P[4].y) <= HIT_TOL_PX) return true;
      return false;
    }
    default:
      return false;
  }
}

// Standard point-to-segment distance helper.
function pointSegmentDist(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx, cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}

// ──────────────────────────────────────────────────────────────────────
//  Renderer — single entry point. Called from a useEffect in HeatmapCanvas
//  that depends on [drawings, draft, viewRange, candles.length, chartW, chartH].
// ──────────────────────────────────────────────────────────────────────

/**
 * Clear and redraw every drawing + the active draft on the given context.
 * Caller is responsible for sizing the canvas / setting DPR transforms.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array} drawings
 * @param {object|null} draft       In-progress drawing (rendered semi-transparent).
 * @param {{x:number,y:number}|null} eraserPointer  When eraser is active, the
 *                                                   pointer position so we can
 *                                                   highlight the hover target.
 * @param {object} ctxCoords        { viewRange, chartW, chartH, nCandles }
 */
export function renderAll(ctx, drawings, draft, eraserPointer, ctxCoords, selectedId) {
  const { chartW, chartH } = ctxCoords;
  ctx.clearRect(0, 0, chartW + 1, chartH + 1);
  // Permanent drawings (full opacity). Skip any individually `hidden`
  // by the right-click context menu (B3b).
  for (const d of drawings) {
    if (d.hidden) continue;
    drawOne(ctx, d, ctxCoords, /* opacity */ 1, /* dashed */ false);
    // Locked-drawing badge: a small lock glyph at the first anchor so
    // the user gets a visual cue that this primitive is read-only.
    if (d.locked) {
      const anchor = d.a || (d.points && d.points[0]);
      if (anchor) {
        const P = anchorToPixel(anchor, ctxCoords);
        ctx.save();
        ctx.font = "10px ui-sans-serif, system-ui";
        ctx.fillStyle = "rgba(250,204,21,0.95)";
        ctx.shadowBlur = 4;
        ctx.shadowColor = "rgba(0,0,0,0.7)";
        ctx.fillText("🔒", P.x + 4, P.y - 4);
        ctx.restore();
      }
    }
  }
  // Active draft (semi-transparent + dashed to feel "ghosty").
  if (draft) {
    drawOne(ctx, draft, ctxCoords, /* opacity */ 0.85, /* dashed */ true);
  }
  // Eraser hover highlight — red halo on the drawing under the pointer.
  if (eraserPointer) {
    const hit = hitTest(drawings, eraserPointer.x, eraserPointer.y, ctxCoords);
    if (hit && !hit.locked) {
      ctx.save();
      ctx.shadowBlur = 14;
      ctx.shadowColor = "rgba(239,68,68,0.85)";
      drawOne(ctx, hit, ctxCoords, 1, false, "#ef4444", 2.5);
      ctx.restore();
    }
  }
  // Right-click selection halo (B3b) — cyan dashed outline so user sees
  // which drawing the contextual menu is acting on.
  if (selectedId) {
    const sel = drawings.find((d) => d.id === selectedId);
    if (sel) {
      ctx.save();
      ctx.shadowBlur = 10;
      ctx.shadowColor = "rgba(34,211,238,0.9)";
      drawOne(ctx, sel, ctxCoords, 1, /* dashed */ true, "#22d3ee", 2.2);
      ctx.restore();
    }
  }
}

function drawOne(ctx, d, ctxCoords, opacity, dashed, colorOverride, widthOverride) {
  const color = colorOverride || d.color || DEFAULT_STYLE.color;
  const width = widthOverride || d.width || DEFAULT_STYLE.width;
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = width;
  ctx.setLineDash(dashed ? [6, 4] : []);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  const { chartW, chartH } = ctxCoords;

  switch (d.type) {
    case "line": {
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      ctx.beginPath();
      ctx.moveTo(A.x, A.y);
      ctx.lineTo(B.x, B.y);
      ctx.stroke();
      break;
    }
    case "horizontal": {
      const y = yOfPrice(d.a.price, ctxCoords.viewRange, chartH);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(chartW, y);
      ctx.stroke();
      // Price label on the right
      ctx.setLineDash([]);
      ctx.font = "bold 10px ui-monospace, monospace";
      ctx.textBaseline = "middle";
      const label = formatPrice(d.a.price);
      const tw = ctx.measureText(label).width + 8;
      ctx.fillStyle = color;
      ctx.globalAlpha = opacity * 0.85;
      ctx.fillRect(chartW - tw - 2, y - 8, tw, 16);
      ctx.fillStyle = "#0a0a0c";
      ctx.fillText(label, chartW - tw + 2, y + 1);
      break;
    }
    case "vertical": {
      const x = xOfIdx(d.a.idx, ctxCoords.nCandles, ctxCoords.chartW);
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, chartH);
      ctx.stroke();
      break;
    }
    case "rect": {
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      const x = Math.min(A.x, B.x);
      const y = Math.min(A.y, B.y);
      const w = Math.abs(B.x - A.x);
      const h = Math.abs(B.y - A.y);
      // Fill (alpha) + stroke (full opacity).
      ctx.save();
      ctx.globalAlpha = opacity * (d.fillAlpha != null ? d.fillAlpha : DEFAULT_STYLE.fillAlpha);
      ctx.fillRect(x, y, w, h);
      ctx.restore();
      ctx.strokeRect(x, y, w, h);
      break;
    }
    case "text": {
      const P = anchorToPixel(d.a, ctxCoords);
      const size = d.textSize || DEFAULT_STYLE.textSize;
      ctx.font = `bold ${size}px ui-sans-serif, system-ui, -apple-system`;
      ctx.textBaseline = "alphabetic";
      ctx.fillStyle = color;
      // Soft shadow so text reads over heatmap.
      ctx.shadowBlur = 6;
      ctx.shadowColor = "rgba(0,0,0,0.7)";
      // Flag/signpost variant: same `text` storage type but with an
      // optional `icon` prefix that paints to the LEFT of the label.
      const renderText = (d.icon ? `${d.icon} ` : "") + (d.text || "(text)");
      ctx.fillText(renderText, P.x, P.y);
      ctx.shadowBlur = 0;
      break;
    }
    // ── Fase B2 — Advanced drawings ───────────────────────────────
    case "measure": {
      // Transient gauge — straight line + Δprice / Δ% / Δi banner near
      // the end anchor. Color flips green when going UP, red going DOWN.
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      const goingUp = d.b.price >= d.a.price;
      const gaugeCol = goingUp ? "#22c55e" : "#ef4444";
      ctx.strokeStyle = gaugeCol;
      ctx.fillStyle = gaugeCol;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(A.x, A.y);
      ctx.lineTo(B.x, B.y);
      ctx.stroke();
      ctx.setLineDash([]);
      // Live metrics box near the end anchor.
      const dp = d.b.price - d.a.price;
      const dpPct = d.a.price !== 0 ? (dp / d.a.price) * 100 : 0;
      const di = Math.round(Math.abs(d.b.idx - d.a.idx));
      const sign = dp >= 0 ? "+" : "−";
      const line1 = `${sign}${formatPrice(Math.abs(dp))} (${sign}${Math.abs(dpPct).toFixed(2)}%)`;
      const line2 = `Δi: ${di} velas`;
      ctx.font = "bold 11px ui-monospace, monospace";
      const w1 = ctx.measureText(line1).width;
      const w2 = ctx.measureText(line2).width;
      const boxW = Math.max(w1, w2) + 14;
      const boxH = 32;
      const bx = Math.min(chartW - boxW - 4, B.x + 10);
      const by = Math.max(4, B.y - boxH - 6);
      ctx.globalAlpha = opacity * 0.95;
      ctx.fillStyle = "rgba(8,5,8,0.92)";
      ctx.fillRect(bx, by, boxW, boxH);
      ctx.strokeStyle = gaugeCol;
      ctx.lineWidth = 1;
      ctx.strokeRect(bx, by, boxW, boxH);
      ctx.fillStyle = gaugeCol;
      ctx.fillText(line1, bx + 7, by + 13);
      ctx.fillStyle = "rgba(226,232,240,0.85)";
      ctx.font = "10px ui-monospace, monospace";
      ctx.fillText(line2, bx + 7, by + 26);
      break;
    }
    case "fib_retracement":
    case "fib_extension": {
      // Two anchors define the 0 (anchor a) and 1.0 (anchor b) levels.
      // Each fib level is rendered as a horizontal segment between the
      // two anchor x-positions (or extended across the full chart width
      // when level<0 or level>1, with a slightly dimmed style outside
      // the anchor range). Right-side badge shows "$price (level)".
      const levels = d.type === "fib_retracement"
        ? (d.levels || DEFAULT_FIB_RETRACEMENT)
        : (d.levels || DEFAULT_FIB_EXTENSION);
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      const xLeft = Math.min(A.x, B.x);
      const xRight = Math.max(A.x, B.x);
      const range = d.b.price - d.a.price; // signed
      ctx.lineWidth = 1.2;
      ctx.font = "bold 10px ui-monospace, monospace";
      for (const lv of levels) {
        const levelPrice = d.a.price + lv * range;
        const y = yOfPrice(levelPrice, ctxCoords.viewRange, chartH);
        if (y < -20 || y > chartH + 20) continue;
        const c = fibColor(lv, color, d.colorIsCustom);
        ctx.strokeStyle = c;
        ctx.fillStyle = c;
        // Dashed segment in the anchor zone; extend a thin dotted halo
        // to the right edge so the level is readable across the chart.
        ctx.setLineDash(dashed ? [6, 4] : [2, 3]);
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(chartW, y);
        ctx.stroke();
        ctx.setLineDash([]);
        // Solid emphasis between anchors (a→b range only).
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(xLeft, y);
        ctx.lineTo(xRight, y);
        ctx.stroke();
        ctx.lineWidth = 1.2;
        // Right-side badge — matches the MAX/SUP style used by the heatmap
        // layer (small filled rect + white text inside).
        const label = `${formatPrice(levelPrice)} (${(lv * 100).toFixed(1)}%)`;
        const lw = ctx.measureText(label).width + 10;
        const lh = 14;
        const lx = chartW - lw - 4;
        const ly = y - lh / 2;
        ctx.fillStyle = c;
        ctx.globalAlpha = opacity * 0.92;
        ctx.fillRect(lx, ly, lw, lh);
        ctx.fillStyle = "#0a0a0c";
        ctx.fillText(label, lx + 5, ly + 10);
        ctx.globalAlpha = opacity;
      }
      break;
    }
    case "channel": {
      // a→b is the main trendline; `c` is an offset anchor whose
      // perpendicular distance from a→b defines the parallel line.
      if (!d.b || !d.c) {
        // Multi-step draft preview before all anchors land. Show what
        // we have so the user sees the in-progress geometry.
        if (d.b) {
          const A = anchorToPixel(d.a, ctxCoords);
          const B = anchorToPixel(d.b, ctxCoords);
          ctx.strokeStyle = color;
          ctx.lineWidth = width;
          ctx.setLineDash([4, 4]);
          ctx.beginPath();
          ctx.moveTo(A.x, A.y); ctx.lineTo(B.x, B.y); ctx.stroke();
          ctx.setLineDash([]);
        }
        break;
      }
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      const C = anchorToPixel(d.c, ctxCoords);
      // Offset vector = perpendicular from line A→B to point C.
      // Using vector projection: dx,dy is the line direction unit.
      const lineDX = B.x - A.x, lineDY = B.y - A.y;
      const len = Math.hypot(lineDX, lineDY) || 1;
      const ux = lineDX / len, uy = lineDY / len;
      // Project AC onto line AB to find the foot of perpendicular.
      const acx = C.x - A.x, acy = C.y - A.y;
      const t = acx * ux + acy * uy;
      const footX = A.x + t * ux, footY = A.y + t * uy;
      const offX = C.x - footX, offY = C.y - footY;
      // Parallel line: A+off → B+off.
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = width;
      // Main line
      ctx.beginPath();
      ctx.moveTo(A.x, A.y);
      ctx.lineTo(B.x, B.y);
      ctx.stroke();
      // Parallel line
      ctx.beginPath();
      ctx.moveTo(A.x + offX, A.y + offY);
      ctx.lineTo(B.x + offX, B.y + offY);
      ctx.stroke();
      // Soft fill between
      ctx.save();
      ctx.globalAlpha = opacity * 0.08;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(A.x, A.y);
      ctx.lineTo(B.x, B.y);
      ctx.lineTo(B.x + offX, B.y + offY);
      ctx.lineTo(A.x + offX, A.y + offY);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      break;
    }
    case "position": {
      // a = entry, b = target, c = stop. Render two horizontal bands
      // bracketing entry (green to target, red to stop) and a header
      // chip with R:R ratio + %gain / %loss.
      if (!d.b) break; // need at least entry+target preview
      const entry = d.a;
      const target = d.b;
      const stop = d.c; // may be undefined during step 2
      const xLeft = 0;
      const xRight = chartW;
      const yE = yOfPrice(entry.price, ctxCoords.viewRange, chartH);
      const yT = yOfPrice(target.price, ctxCoords.viewRange, chartH);
      // Green profit zone (between entry & target).
      ctx.save();
      ctx.globalAlpha = opacity * 0.18;
      ctx.fillStyle = "#22c55e";
      ctx.fillRect(xLeft, Math.min(yE, yT), xRight - xLeft, Math.abs(yT - yE));
      ctx.restore();
      // Red loss zone (between entry & stop) — only when stop is set.
      if (stop) {
        const yS = yOfPrice(stop.price, ctxCoords.viewRange, chartH);
        ctx.save();
        ctx.globalAlpha = opacity * 0.18;
        ctx.fillStyle = "#ef4444";
        ctx.fillRect(xLeft, Math.min(yE, yS), xRight - xLeft, Math.abs(yS - yE));
        ctx.restore();
      }
      // Entry line + label.
      ctx.strokeStyle = "#e2e8f0";
      ctx.fillStyle = "#e2e8f0";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.beginPath(); ctx.moveTo(xLeft, yE); ctx.lineTo(xRight, yE); ctx.stroke();
      ctx.setLineDash([]);
      // R:R metrics chip near the right of entry line.
      const gainPct = entry.price !== 0 ? ((target.price - entry.price) / entry.price) * 100 : 0;
      const lossPct = (entry.price !== 0 && stop) ? ((stop.price - entry.price) / entry.price) * 100 : null;
      let rr = null;
      if (stop) {
        const denom = Math.abs(entry.price - stop.price);
        rr = denom > 0 ? Math.abs(target.price - entry.price) / denom : null;
      }
      const isLong = target.price >= entry.price;
      const labelL1 = `${isLong ? "LONG" : "SHORT"} · ENTRY ${formatPrice(entry.price)}`;
      const labelL2 = `🎯 ${formatPrice(target.price)} (${gainPct >= 0 ? "+" : ""}${gainPct.toFixed(2)}%)`;
      const labelL3 = stop ? `🛑 ${formatPrice(stop.price)} (${lossPct >= 0 ? "+" : ""}${lossPct.toFixed(2)}%)` : null;
      const labelRR = rr != null ? `R:R = ${rr.toFixed(2)}` : null;
      ctx.font = "bold 11px ui-monospace, monospace";
      const lines = [labelL1, labelL2, labelL3, labelRR].filter(Boolean);
      const lw = Math.max(...lines.map((s) => ctx.measureText(s).width)) + 14;
      const lh = lines.length * 14 + 6;
      const bx = 8;
      const by = Math.max(4, yE - lh - 8);
      ctx.fillStyle = "rgba(8,5,8,0.92)";
      ctx.fillRect(bx, by, lw, lh);
      ctx.strokeStyle = isLong ? "#22c55e" : "#ef4444";
      ctx.lineWidth = 1;
      ctx.strokeRect(bx, by, lw, lh);
      const lineColors = ["#e2e8f0", "#22c55e", "#ef4444", "#22d3ee"];
      lines.forEach((s, i) => {
        ctx.fillStyle = lineColors[i] || "#e2e8f0";
        ctx.fillText(s, bx + 7, by + 14 + i * 14);
      });
      break;
    }
    // ── Fase B3a — Shapes & annotations ───────────────────────────
    case "circle": {
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      const r = Math.hypot(B.x - A.x, B.y - A.y);
      ctx.beginPath();
      ctx.arc(A.x, A.y, r, 0, Math.PI * 2);
      ctx.stroke();
      // Subtle fill so the shape reads against the heatmap.
      ctx.save();
      ctx.globalAlpha = opacity * (d.fillAlpha != null ? d.fillAlpha : DEFAULT_STYLE.fillAlpha);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.restore();
      break;
    }
    case "ellipse": {
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      const cx = (A.x + B.x) / 2;
      const cy = (A.y + B.y) / 2;
      const rx = Math.abs(B.x - A.x) / 2;
      const ry = Math.abs(B.y - A.y) / 2;
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.save();
      ctx.globalAlpha = opacity * (d.fillAlpha != null ? d.fillAlpha : DEFAULT_STYLE.fillAlpha);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.restore();
      break;
    }
    case "triangle": {
      if (!d.b) break;
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      ctx.beginPath();
      ctx.moveTo(A.x, A.y);
      ctx.lineTo(B.x, B.y);
      if (d.c) {
        const C = anchorToPixel(d.c, ctxCoords);
        ctx.lineTo(C.x, C.y);
        ctx.closePath();
        ctx.save();
        ctx.globalAlpha = opacity * (d.fillAlpha != null ? d.fillAlpha : DEFAULT_STYLE.fillAlpha);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.restore();
      }
      ctx.stroke();
      break;
    }
    case "freehand": {
      if (!d.points || d.points.length < 2) break;
      ctx.beginPath();
      const first = anchorToPixel(d.points[0], ctxCoords);
      ctx.moveTo(first.x, first.y);
      for (let i = 1; i < d.points.length; i++) {
        const p = anchorToPixel(d.points[i], ctxCoords);
        ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
      break;
    }
    case "polyline": {
      if (!d.points || d.points.length < 1) break;
      ctx.beginPath();
      const first = anchorToPixel(d.points[0], ctxCoords);
      ctx.moveTo(first.x, first.y);
      for (let i = 1; i < d.points.length; i++) {
        const p = anchorToPixel(d.points[i], ctxCoords);
        ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
      // Draft preview: dashed segment from last vertex to live cursor.
      if (d.previewPt) {
        const last = anchorToPixel(d.points[d.points.length - 1], ctxCoords);
        const pv = anchorToPixel(d.previewPt, ctxCoords);
        ctx.save();
        ctx.setLineDash([4, 4]);
        ctx.globalAlpha = opacity * 0.65;
        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(pv.x, pv.y);
        ctx.stroke();
        ctx.restore();
      }
      // Small handle markers at each vertex.
      for (const pt of d.points) {
        const p = anchorToPixel(pt, ctxCoords);
        ctx.save();
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      break;
    }
    case "callout": {
      if (!d.b) break;
      // a = arrow target tip, b = text-box anchor (top-left of label).
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      const label = d.text || "(callout)";
      ctx.font = `bold ${d.textSize || DEFAULT_STYLE.textSize}px ui-sans-serif, system-ui`;
      const tw = ctx.measureText(label).width;
      const pad = 6;
      const boxW = tw + pad * 2;
      const boxH = (d.textSize || DEFAULT_STYLE.textSize) + pad * 2;
      // Connector line from B (box) to A (tip).
      ctx.beginPath();
      ctx.moveTo(B.x + boxW / 2, B.y + boxH / 2);
      ctx.lineTo(A.x, A.y);
      ctx.stroke();
      // Arrowhead at A.
      const ang = Math.atan2(A.y - (B.y + boxH / 2), A.x - (B.x + boxW / 2));
      const ahL = 8;
      ctx.beginPath();
      ctx.moveTo(A.x, A.y);
      ctx.lineTo(A.x - ahL * Math.cos(ang - 0.4), A.y - ahL * Math.sin(ang - 0.4));
      ctx.lineTo(A.x - ahL * Math.cos(ang + 0.4), A.y - ahL * Math.sin(ang + 0.4));
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
      // Label box.
      ctx.save();
      ctx.fillStyle = "rgba(8,5,8,0.92)";
      ctx.fillRect(B.x, B.y, boxW, boxH);
      ctx.strokeRect(B.x, B.y, boxW, boxH);
      ctx.fillStyle = color;
      ctx.textBaseline = "alphabetic";
      ctx.fillText(label, B.x + pad, B.y + (d.textSize || DEFAULT_STYLE.textSize) + pad - 1);
      ctx.restore();
      break;
    }
    // ── Fase B4a — Advanced Fibonacci + Harmonic patterns + Elliott ──
    case "fib_timezone": {
      // 1-anchor. Vertical lines at idx + Fib(n).
      const A = anchorToPixel(d.a, ctxCoords);
      ctx.font = "bold 9px ui-monospace, monospace";
      for (const off of FIB_TIMEZONE_OFFSETS) {
        const xv = xOfIdx(d.a.idx + off, ctxCoords.nCandles, ctxCoords.chartW);
        if (xv < 0 || xv > ctxCoords.chartW) continue;
        ctx.save();
        ctx.setLineDash(dashed ? [6,4] : [2,3]);
        ctx.strokeStyle = color;
        ctx.beginPath();
        ctx.moveTo(xv, 0); ctx.lineTo(xv, ctxCoords.chartH);
        ctx.stroke();
        ctx.restore();
        ctx.fillStyle = "rgba(8,5,8,0.85)";
        ctx.fillRect(xv - 12, 2, 24, 13);
        ctx.fillStyle = color;
        ctx.fillText(String(off), xv - 8, 12);
      }
      // Anchor marker
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(A.x, A.y, 3, 0, Math.PI*2); ctx.fill();
      break;
    }
    case "fib_speedfan": {
      // 2 anchors. Lines from A through fractions 1/8...7/8 of the A→B box.
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      ctx.lineWidth = 1.2;
      for (const f of EIGHTHS_AND_THIRDS) {
        // Two families: vertical-division (target X = B.x, varied y) and horizontal.
        const tgtX = B.x;
        const tgtY = A.y + f * (B.y - A.y);
        const tgtX2 = A.x + f * (B.x - A.x);
        const tgtY2 = B.y;
        ctx.strokeStyle = fibColor(f, color, d.colorIsCustom);
        ctx.beginPath();
        ctx.moveTo(A.x, A.y); ctx.lineTo(tgtX, tgtY); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(A.x, A.y); ctx.lineTo(tgtX2, tgtY2); ctx.stroke();
      }
      // Trendline 1/1 reference.
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.8;
      ctx.beginPath(); ctx.moveTo(A.x, A.y); ctx.lineTo(B.x, B.y); ctx.stroke();
      break;
    }
    case "fib_wedge": {
      // 2 trendlines from a common origin (A), opening at fib retracement ratios
      // toward B. We use the slope of A→B as 1.0 and draw additional lines at
      // 0.382 / 0.5 / 0.618 / 1.272 / 1.618 of that slope.
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      const dx = B.x - A.x, dy = B.y - A.y;
      const ratios = [0.382, 0.5, 0.618, 1.0, 1.272, 1.618];
      ctx.lineWidth = 1.3;
      for (const r of ratios) {
        ctx.strokeStyle = fibColor(r, color, d.colorIsCustom);
        ctx.beginPath();
        ctx.moveTo(A.x, A.y);
        ctx.lineTo(A.x + dx, A.y + dy * r);
        ctx.stroke();
      }
      break;
    }
    case "xabcd":
    case "cypher":
    case "elliott_impulse": {
      // 5-anchor connected segments + labels. Cypher uses informational
      // ratio hints; XABCD shows raw leg ratios; Elliott impulse uses 1-5.
      if (!d.points || d.points.length < 1) break;
      const labels = d.type === "elliott_impulse" ? ["1","2","3","4","5"] : ["X","A","B","C","D"];
      const pts = d.points;
      ctx.lineWidth = 1.6;
      ctx.font = "bold 11px ui-sans-serif, system-ui";
      for (let i = 0; i < pts.length; i++) {
        const P = anchorToPixel(pts[i], ctxCoords);
        if (i > 0) {
          const Pp = anchorToPixel(pts[i - 1], ctxCoords);
          ctx.beginPath();
          ctx.moveTo(Pp.x, Pp.y); ctx.lineTo(P.x, P.y);
          ctx.stroke();
          // Leg ratio label for xabcd/cypher (middle of segment).
          if ((d.type === "xabcd" || d.type === "cypher") && i >= 2) {
            const prev = pts[i - 1].price - pts[i - 2].price;
            const cur = pts[i].price - pts[i - 1].price;
            const ratio = prev !== 0 ? Math.abs(cur / prev).toFixed(3) : "—";
            const midX = (Pp.x + P.x) / 2;
            const midY = (Pp.y + P.y) / 2;
            ctx.save();
            ctx.fillStyle = "rgba(8,5,8,0.88)";
            const txt = `${labels[i-1]}${labels[i]}: ${ratio}`;
            const tw = ctx.measureText(txt).width + 8;
            ctx.fillRect(midX - tw / 2, midY - 8, tw, 14);
            ctx.fillStyle = color;
            ctx.fillText(txt, midX - tw / 2 + 4, midY + 3);
            ctx.restore();
          }
        }
        // Vertex label
        ctx.save();
        ctx.fillStyle = color;
        ctx.beginPath(); ctx.arc(P.x, P.y, 3.5, 0, Math.PI*2); ctx.fill();
        ctx.fillStyle = "#0a0a0c";
        ctx.fillRect(P.x + 5, P.y - 9, 14, 14);
        ctx.fillStyle = color;
        ctx.fillText(labels[i] || String(i+1), P.x + 7, P.y + 2);
        ctx.restore();
      }
      // Live preview ghost segment to cursor.
      if (d.previewPt && pts.length > 0 && pts.length < 5) {
        const last = anchorToPixel(pts[pts.length-1], ctxCoords);
        const pv = anchorToPixel(d.previewPt, ctxCoords);
        ctx.save();
        ctx.setLineDash([5,4]);
        ctx.globalAlpha = opacity * 0.6;
        ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(pv.x, pv.y); ctx.stroke();
        ctx.restore();
      }
      break;
    }
    case "three_drives":
    case "elliott_correction": {
      // 3-anchor draft → 2 segments (A→B→C). Labels:
      //   three_drives: "D1","D2","D3"
      //   elliott_correction: "A","B","C"
      if (!d.b) break;
      const labels = d.type === "elliott_correction" ? ["A","B","C"] : ["D1","D2","D3"];
      const pts = [d.a, d.b, d.c].filter(Boolean);
      ctx.lineWidth = 1.6;
      ctx.font = "bold 11px ui-sans-serif, system-ui";
      for (let i = 0; i < pts.length; i++) {
        const P = anchorToPixel(pts[i], ctxCoords);
        if (i > 0) {
          const Pp = anchorToPixel(pts[i-1], ctxCoords);
          ctx.beginPath(); ctx.moveTo(Pp.x, Pp.y); ctx.lineTo(P.x, P.y); ctx.stroke();
        }
        ctx.fillStyle = color;
        ctx.beginPath(); ctx.arc(P.x, P.y, 3.5, 0, Math.PI*2); ctx.fill();
        ctx.fillStyle = "#0a0a0c";
        const lt = labels[i] || String(i+1);
        const lw = ctx.measureText(lt).width + 6;
        ctx.fillRect(P.x + 5, P.y - 9, lw, 14);
        ctx.fillStyle = color;
        ctx.fillText(lt, P.x + 8, P.y + 2);
      }
      // Three-drives ratio annotation between D1→D2 and D2→D3.
      if (d.type === "three_drives" && d.c) {
        const dA = d.b.price - d.a.price;
        const dB = d.c.price - d.b.price;
        const ratio = dA !== 0 ? Math.abs(dB / dA).toFixed(3) : "—";
        const C = anchorToPixel(d.c, ctxCoords);
        const txt = `D2D3/D1D2: ${ratio}`;
        const tw = ctx.measureText(txt).width + 8;
        ctx.save();
        ctx.fillStyle = "rgba(8,5,8,0.88)";
        ctx.fillRect(C.x + 8, C.y + 10, tw, 14);
        ctx.fillStyle = color;
        ctx.fillText(txt, C.x + 12, C.y + 22);
        ctx.restore();
      }
      break;
    }
    // ── Fase B4b — Gann tools ─────────────────────────────────────
    case "gann_fan": {
      // 2 anchors (A, B). A→B defines the 1x1 unit (Δidx, Δprice).
      // For each ratio (n, m) we draw a ray from A in chart-space
      // direction (n·Δidx, m·Δprice) — i.e. pixel direction (n·dx, m·dy).
      // Each ray is extended 3× past the unit segment so it fills more
      // of the visible chart; further clipping is implicit via canvas.
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      const dx = B.x - A.x;
      const dy = B.y - A.y;
      if (dx === 0 && dy === 0) break;
      const EXTEND = 3; // ray length = 3 × unit
      ctx.font = "bold 10px ui-monospace, monospace";
      for (const [n, m] of GANN_FAN_RATIOS) {
        const key = `${n}x${m}`;
        const rayCol = GANN_FAN_COLORS[key] || color;
        const ex = A.x + EXTEND * n * dx;
        const ey = A.y + EXTEND * m * dy;
        ctx.strokeStyle = rayCol;
        ctx.lineWidth = (n === 1 && m === 1) ? 1.8 : 1.2;
        ctx.beginPath();
        ctx.moveTo(A.x, A.y);
        ctx.lineTo(ex, ey);
        ctx.stroke();
        // Label at the unit point (A + unit-vector) so it stays on-screen
        // even when the ray extends past the chart edge.
        const lx = A.x + n * dx;
        const ly = A.y + m * dy;
        // Skip drawing the badge if it lands far outside the chart.
        if (lx >= -10 && lx <= chartW + 10 && ly >= -10 && ly <= chartH + 10) {
          const lw = ctx.measureText(key).width + 8;
          ctx.save();
          ctx.fillStyle = "rgba(8,5,8,0.88)";
          ctx.fillRect(lx + 4, ly - 7, lw, 13);
          ctx.fillStyle = rayCol;
          ctx.fillText(key, lx + 8, ly + 3);
          ctx.restore();
        }
      }
      // Anchor marker (small filled dot at A).
      ctx.save();
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(A.x, A.y, 3, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
      break;
    }
    case "gann_box": {
      // 2 anchors define opposite corners of the box. Interior is
      // divided by EIGHTHS_AND_THIRDS on BOTH axes (mirrors the Fib
      // Speed Resistance Fan internal grid). Both main diagonals are
      // also drawn — these are the Gann "time × price" balance lines.
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      const xMin = Math.min(A.x, B.x), xMax = Math.max(A.x, B.x);
      const yMin = Math.min(A.y, B.y), yMax = Math.max(A.y, B.y);
      const w = xMax - xMin;
      const h = yMax - yMin;
      // Outer border + soft fill.
      ctx.save();
      ctx.globalAlpha = opacity * (d.fillAlpha != null ? d.fillAlpha : DEFAULT_STYLE.fillAlpha);
      ctx.fillStyle = color;
      ctx.fillRect(xMin, yMin, w, h);
      ctx.restore();
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.strokeRect(xMin, yMin, w, h);
      // Interior grid (octaves + thirds). Use thinner stroke + alpha so
      // the heatmap is still readable through the grid.
      ctx.save();
      ctx.globalAlpha = opacity * 0.55;
      ctx.lineWidth = 0.8;
      ctx.setLineDash(dashed ? [4, 4] : [2, 3]);
      ctx.font = "9px ui-monospace, monospace";
      for (const f of EIGHTHS_AND_THIRDS) {
        // Vertical division
        const xv = xMin + f * w;
        ctx.strokeStyle = fibColor(f, color, d.colorIsCustom);
        ctx.beginPath(); ctx.moveTo(xv, yMin); ctx.lineTo(xv, yMax); ctx.stroke();
        // Horizontal division
        const yv = yMin + f * h;
        ctx.beginPath(); ctx.moveTo(xMin, yv); ctx.lineTo(xMax, yv); ctx.stroke();
      }
      ctx.restore();
      // Both main diagonals — Gann "1x1" balance lines for the box.
      ctx.save();
      ctx.globalAlpha = opacity * 0.85;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.3;
      ctx.beginPath();
      ctx.moveTo(xMin, yMin); ctx.lineTo(xMax, yMax);
      ctx.moveTo(xMin, yMax); ctx.lineTo(xMax, yMin);
      ctx.stroke();
      ctx.restore();
      break;
    }
    // ── Fase B5a — annotations, projections, measurements, manual patterns ──
    case "highlighter": {
      if (!d.points || d.points.length < 2) break;
      ctx.save();
      ctx.globalAlpha = opacity * HIGHLIGHTER_ALPHA;
      ctx.strokeStyle = color;
      ctx.lineWidth = d.width || HIGHLIGHTER_WIDTH;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      const first = anchorToPixel(d.points[0], ctxCoords);
      ctx.moveTo(first.x, first.y);
      for (let i = 1; i < d.points.length; i++) {
        const p = anchorToPixel(d.points[i], ctxCoords);
        ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
      ctx.restore();
      break;
    }
    case "pitchfork": {
      if (!d.b) break;
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      // Preview lines while only A→B is committed.
      if (!d.c) {
        ctx.setLineDash([4, 4]);
        ctx.beginPath(); ctx.moveTo(A.x, A.y); ctx.lineTo(B.x, B.y); ctx.stroke();
        ctx.setLineDash([]);
        break;
      }
      const C = anchorToPixel(d.c, ctxCoords);
      const midX = (B.x + C.x) / 2;
      const midY = (B.y + C.y) / 2;
      const dxR = midX - A.x;
      const dyR = midY - A.y;
      // Soft fill between rails.
      ctx.save();
      ctx.globalAlpha = opacity * 0.06;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(B.x, B.y);
      ctx.lineTo(B.x + dxR, B.y + dyR);
      ctx.lineTo(C.x + dxR, C.y + dyR);
      ctx.lineTo(C.x, C.y);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      ctx.lineWidth = width;
      // Median (heavy)
      ctx.lineWidth = (width || 1.5) + 0.4;
      ctx.beginPath(); ctx.moveTo(A.x, A.y); ctx.lineTo(midX + dxR * 0.4, midY + dyR * 0.4); ctx.stroke();
      // Rails (parallel through B and C)
      ctx.lineWidth = width;
      ctx.beginPath(); ctx.moveTo(B.x, B.y); ctx.lineTo(B.x + dxR, B.y + dyR); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(C.x, C.y); ctx.lineTo(C.x + dxR, C.y + dyR); ctx.stroke();
      // Anchor dots
      for (const P of [A, B, C]) {
        ctx.beginPath(); ctx.arc(P.x, P.y, 3, 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case "fib_channel": {
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      // Base trendline (cyan)
      ctx.strokeStyle = color;
      ctx.lineWidth = width + 0.4;
      ctx.beginPath(); ctx.moveTo(A.x, A.y); ctx.lineTo(B.x, B.y); ctx.stroke();
      // Parallels offset perpendicularly. Perpendicular pixel-vector to A→B.
      const dxL = B.x - A.x, dyL = B.y - A.y;
      const len = Math.hypot(dxL, dyL) || 1;
      const nx = -dyL / len, ny = dxL / len;
      // Translate price-delta into pixel offset using yOfPrice diff.
      const yAtA1 = anchorToPixel({ idx: d.a.idx, price: d.b.price }, ctxCoords).y - A.y;
      // Use yAtA1 magnitude as 1.0 reference — fib ratios scale this.
      const refMag = yAtA1;
      ctx.lineWidth = width;
      ctx.font = "bold 9px ui-monospace, monospace";
      for (const r of FIB_CHANNEL_RATIOS) {
        const offMag = r * refMag;
        const ox = nx * offMag;
        const oy = ny * offMag;
        const lc = fibColor(r, color, d.colorIsCustom);
        ctx.strokeStyle = lc;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(A.x + ox, A.y + oy);
        ctx.lineTo(B.x + ox, B.y + oy);
        ctx.stroke();
        ctx.setLineDash([]);
        // Right-side ratio label.
        const lbl = `${(r * 100).toFixed(1)}%`;
        const lw = ctx.measureText(lbl).width + 6;
        ctx.fillStyle = lc;
        ctx.fillRect(B.x + ox + 2, B.y + oy - 6, lw, 12);
        ctx.fillStyle = "#0a0a0c";
        ctx.fillText(lbl, B.x + ox + 5, B.y + oy + 3);
      }
      break;
    }
    case "fib_extension_trend": {
      if (!d.b) break;
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      // Segment A→B
      ctx.lineWidth = width + 0.3;
      ctx.beginPath(); ctx.moveTo(A.x, A.y); ctx.lineTo(B.x, B.y); ctx.stroke();
      // Vertex dots A, B
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(A.x, A.y, 3, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(B.x, B.y, 3, 0, Math.PI * 2); ctx.fill();
      ctx.font = "bold 10px ui-monospace, monospace";
      ctx.fillStyle = "#0a0a0c";
      ctx.fillRect(A.x + 5, A.y - 8, 14, 13); ctx.fillStyle = color; ctx.fillText("A", A.x + 8, A.y + 2);
      ctx.fillStyle = "#0a0a0c"; ctx.fillRect(B.x + 5, B.y - 8, 14, 13); ctx.fillStyle = color; ctx.fillText("B", B.x + 8, B.y + 2);
      if (!d.c) break;
      const C = anchorToPixel(d.c, ctxCoords);
      ctx.lineWidth = width;
      ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(B.x, B.y); ctx.lineTo(C.x, C.y); ctx.stroke();
      ctx.setLineDash([]);
      // Vertex dot + label C
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(C.x, C.y, 3, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#0a0a0c"; ctx.fillRect(C.x + 5, C.y - 8, 14, 13);
      ctx.fillStyle = color; ctx.fillText("C", C.x + 8, C.y + 2);
      // Project ratios of (B.price - A.price) from C.
      const range = d.b.price - d.a.price;
      for (const r of FIB_EXT_TREND_RATIOS) {
        const lvPrice = d.c.price + r * range;
        const ly = yOfPrice(lvPrice, ctxCoords.viewRange, chartH);
        if (ly < -10 || ly > chartH + 10) continue;
        const lc = fibColor(r, color, d.colorIsCustom);
        ctx.strokeStyle = lc;
        ctx.setLineDash([2, 3]);
        ctx.beginPath();
        ctx.moveTo(C.x, ly);
        ctx.lineTo(chartW, ly);
        ctx.stroke();
        ctx.setLineDash([]);
        const lbl = `${formatPrice(lvPrice)} (${(r * 100).toFixed(1)}%)`;
        const lw = ctx.measureText(lbl).width + 10;
        ctx.fillStyle = lc; ctx.fillRect(chartW - lw - 4, ly - 7, lw, 14);
        ctx.fillStyle = "#0a0a0c"; ctx.fillText(lbl, chartW - lw + 1, ly + 4);
      }
      break;
    }
    case "forecast": {
      // Render as a dashed projection chain — visually distinct from
      // a normal polyline. xOfIdx may extrapolate (idx > nCandles-1),
      // and the line will scroll into view as the chart pans.
      if (!d.points || d.points.length < 1) break;
      ctx.setLineDash([6, 4]);
      ctx.lineWidth = width;
      ctx.beginPath();
      const first = anchorToPixel(d.points[0], ctxCoords);
      ctx.moveTo(first.x, first.y);
      for (let i = 1; i < d.points.length; i++) {
        const p = anchorToPixel(d.points[i], ctxCoords);
        ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
      ctx.setLineDash([]);
      // Vertex markers + small "FCST" label near first point.
      for (const pt of d.points) {
        const p = anchorToPixel(pt, ctxCoords);
        ctx.fillStyle = color;
        ctx.beginPath(); ctx.arc(p.x, p.y, 2.5, 0, Math.PI * 2); ctx.fill();
      }
      ctx.font = "bold 9px ui-monospace, monospace";
      ctx.fillStyle = "#0a0a0c";
      ctx.fillRect(first.x + 6, first.y - 9, 32, 13);
      ctx.fillStyle = color;
      ctx.fillText("FCST", first.x + 9, first.y + 1);
      // Live preview ghost segment.
      if (d.previewPt) {
        const last = anchorToPixel(d.points[d.points.length - 1], ctxCoords);
        const pv = anchorToPixel(d.previewPt, ctxCoords);
        ctx.save();
        ctx.setLineDash([3, 3]);
        ctx.globalAlpha = opacity * 0.6;
        ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(pv.x, pv.y); ctx.stroke();
        ctx.restore();
      }
      break;
    }
    case "price_range": {
      const yA = yOfPrice(d.a.price, ctxCoords.viewRange, chartH);
      const yB = yOfPrice(d.b.price, ctxCoords.viewRange, chartH);
      const goingUp = d.b.price >= d.a.price;
      const bandCol = goingUp ? "#22c55e" : "#ef4444";
      ctx.save();
      ctx.globalAlpha = opacity * 0.10;
      ctx.fillStyle = bandCol;
      ctx.fillRect(0, Math.min(yA, yB), chartW, Math.abs(yB - yA));
      ctx.restore();
      ctx.strokeStyle = bandCol;
      ctx.lineWidth = 1.4;
      ctx.setLineDash([6, 3]);
      ctx.beginPath(); ctx.moveTo(0, yA); ctx.lineTo(chartW, yA); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, yB); ctx.lineTo(chartW, yB); ctx.stroke();
      ctx.setLineDash([]);
      // Label box (top-right): ΔP, Δ%
      const dp = d.b.price - d.a.price;
      const dpPct = d.a.price !== 0 ? (dp / d.a.price) * 100 : 0;
      const sign = dp >= 0 ? "+" : "−";
      const line1 = `ΔP ${sign}${formatPrice(Math.abs(dp))}`;
      const line2 = `${sign}${Math.abs(dpPct).toFixed(2)}%`;
      ctx.font = "bold 11px ui-monospace, monospace";
      const lw = Math.max(ctx.measureText(line1).width, ctx.measureText(line2).width) + 12;
      const lh = 30;
      const lx = chartW - lw - 4;
      const ly = Math.min(yA, yB) + 4;
      ctx.fillStyle = "rgba(8,5,8,0.92)"; ctx.fillRect(lx, ly, lw, lh);
      ctx.strokeStyle = bandCol; ctx.lineWidth = 1; ctx.strokeRect(lx, ly, lw, lh);
      ctx.fillStyle = bandCol; ctx.fillText(line1, lx + 6, ly + 12);
      ctx.fillStyle = "rgba(226,232,240,0.9)"; ctx.fillText(line2, lx + 6, ly + 25);
      break;
    }
    case "date_range": {
      const xA = xOfIdx(d.a.idx, ctxCoords.nCandles, ctxCoords.chartW);
      const xB = xOfIdx(d.b.idx, ctxCoords.nCandles, ctxCoords.chartW);
      const bandCol = "#22d3ee";
      ctx.save();
      ctx.globalAlpha = opacity * 0.08;
      ctx.fillStyle = bandCol;
      ctx.fillRect(Math.min(xA, xB), 0, Math.abs(xB - xA), chartH);
      ctx.restore();
      ctx.strokeStyle = bandCol;
      ctx.lineWidth = 1.4;
      ctx.setLineDash([6, 3]);
      ctx.beginPath(); ctx.moveTo(xA, 0); ctx.lineTo(xA, chartH); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(xB, 0); ctx.lineTo(xB, chartH); ctx.stroke();
      ctx.setLineDash([]);
      const di = Math.abs(Math.round(d.b.idx - d.a.idx));
      const txt = `Δi ${di} velas`;
      ctx.font = "bold 11px ui-monospace, monospace";
      const tw = ctx.measureText(txt).width + 12;
      const tx = Math.min(xA, xB) + 6;
      const ty = 6;
      ctx.fillStyle = "rgba(8,5,8,0.92)"; ctx.fillRect(tx, ty, tw, 18);
      ctx.strokeStyle = bandCol; ctx.lineWidth = 1; ctx.strokeRect(tx, ty, tw, 18);
      ctx.fillStyle = bandCol; ctx.fillText(txt, tx + 6, ty + 13);
      break;
    }
    case "date_price_range": {
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      const x = Math.min(A.x, B.x), y = Math.min(A.y, B.y);
      const w = Math.abs(B.x - A.x), h = Math.abs(B.y - A.y);
      const goingUp = d.b.price >= d.a.price;
      const bandCol = goingUp ? "#22c55e" : "#ef4444";
      ctx.save();
      ctx.globalAlpha = opacity * 0.10;
      ctx.fillStyle = bandCol;
      ctx.fillRect(x, y, w, h);
      ctx.restore();
      ctx.strokeStyle = bandCol;
      ctx.lineWidth = 1.4;
      ctx.setLineDash([6, 3]);
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);
      // Multi-line label box.
      const dp = d.b.price - d.a.price;
      const dpPct = d.a.price !== 0 ? (dp / d.a.price) * 100 : 0;
      const di = Math.abs(Math.round(d.b.idx - d.a.idx));
      const sign = dp >= 0 ? "+" : "−";
      const lines = [
        `ΔP ${sign}${formatPrice(Math.abs(dp))}`,
        `${sign}${Math.abs(dpPct).toFixed(2)}%`,
        `Δi ${di} velas`,
      ];
      ctx.font = "bold 11px ui-monospace, monospace";
      const lw = Math.max(...lines.map((s) => ctx.measureText(s).width)) + 12;
      const lh = lines.length * 14 + 6;
      const lx = x + 4;
      const ly = y + 4;
      ctx.fillStyle = "rgba(8,5,8,0.92)"; ctx.fillRect(lx, ly, lw, lh);
      ctx.strokeStyle = bandCol; ctx.lineWidth = 1; ctx.strokeRect(lx, ly, lw, lh);
      lines.forEach((s, i) => {
        ctx.fillStyle = i === 0 ? bandCol : "rgba(226,232,240,0.9)";
        ctx.fillText(s, lx + 6, ly + 14 + i * 14);
      });
      break;
    }
    case "abcd":
    case "triangle_pattern":
    case "head_shoulders": {
      if (!d.points || d.points.length < 1) break;
      let labels = [];
      let drawNeckline = false;
      if (d.type === "abcd") labels = ["A", "B", "C", "D"];
      else if (d.type === "triangle_pattern") labels = ["U1", "U2", "L1", "L2"];
      else { labels = ["LS", "V1", "H", "V2", "RS", "N1", "N2"]; drawNeckline = true; }
      ctx.lineWidth = 1.6;
      ctx.font = "bold 11px ui-sans-serif, system-ui";
      const pts = d.points;
      // Connect segments according to pattern type.
      if (d.type === "triangle_pattern") {
        // Upper trendline = points[0]→points[1], Lower = points[2]→points[3].
        if (pts.length >= 2) {
          const P1 = anchorToPixel(pts[0], ctxCoords);
          const P2 = anchorToPixel(pts[1], ctxCoords);
          ctx.beginPath(); ctx.moveTo(P1.x, P1.y); ctx.lineTo(P2.x, P2.y); ctx.stroke();
        }
        if (pts.length >= 4) {
          const P3 = anchorToPixel(pts[2], ctxCoords);
          const P4 = anchorToPixel(pts[3], ctxCoords);
          ctx.beginPath(); ctx.moveTo(P3.x, P3.y); ctx.lineTo(P4.x, P4.y); ctx.stroke();
        }
      } else if (d.type === "head_shoulders") {
        // Main shape: LS→V1→H→V2→RS.
        for (let i = 1; i < Math.min(5, pts.length); i++) {
          const P1 = anchorToPixel(pts[i - 1], ctxCoords);
          const P2 = anchorToPixel(pts[i], ctxCoords);
          ctx.beginPath(); ctx.moveTo(P1.x, P1.y); ctx.lineTo(P2.x, P2.y); ctx.stroke();
        }
        // Neckline.
        if (drawNeckline && pts.length >= 7) {
          const N1 = anchorToPixel(pts[5], ctxCoords);
          const N2 = anchorToPixel(pts[6], ctxCoords);
          ctx.save();
          ctx.strokeStyle = "#facc15";
          ctx.setLineDash([5, 4]);
          ctx.beginPath();
          ctx.moveTo(N1.x, N1.y); ctx.lineTo(N2.x, N2.y);
          ctx.stroke();
          ctx.restore();
        }
      } else {
        // ABCD: connected chain.
        for (let i = 1; i < pts.length; i++) {
          const P1 = anchorToPixel(pts[i - 1], ctxCoords);
          const P2 = anchorToPixel(pts[i], ctxCoords);
          ctx.beginPath(); ctx.moveTo(P1.x, P1.y); ctx.lineTo(P2.x, P2.y); ctx.stroke();
        }
      }
      // Vertex labels.
      for (let i = 0; i < pts.length; i++) {
        const P = anchorToPixel(pts[i], ctxCoords);
        ctx.save();
        ctx.fillStyle = color;
        ctx.beginPath(); ctx.arc(P.x, P.y, 3.5, 0, Math.PI * 2); ctx.fill();
        const lt = labels[i] || String(i + 1);
        const lw = ctx.measureText(lt).width + 6;
        ctx.fillStyle = "#0a0a0c";
        ctx.fillRect(P.x + 5, P.y - 9, lw, 14);
        ctx.fillStyle = color;
        ctx.fillText(lt, P.x + 8, P.y + 2);
        ctx.restore();
      }
      // ABCD ratio annotation (BC/AB and CD/BC).
      if (d.type === "abcd" && pts.length >= 4) {
        const ab = pts[1].price - pts[0].price;
        const bc = pts[2].price - pts[1].price;
        const cd = pts[3].price - pts[2].price;
        const r1 = ab !== 0 ? Math.abs(bc / ab).toFixed(3) : "—";
        const r2 = bc !== 0 ? Math.abs(cd / bc).toFixed(3) : "—";
        const D = anchorToPixel(pts[3], ctxCoords);
        const txt = `BC/AB:${r1} · CD/BC:${r2}`;
        const tw = ctx.measureText(txt).width + 10;
        ctx.save();
        ctx.fillStyle = "rgba(8,5,8,0.92)"; ctx.fillRect(D.x + 8, D.y + 10, tw, 14);
        ctx.fillStyle = color; ctx.fillText(txt, D.x + 12, D.y + 22);
        ctx.restore();
      }
      // Live preview ghost.
      const needed = d.type === "head_shoulders" ? 7 : 4;
      if (d.previewPt && pts.length > 0 && pts.length < needed) {
        const last = anchorToPixel(pts[pts.length - 1], ctxCoords);
        const pv = anchorToPixel(d.previewPt, ctxCoords);
        ctx.save();
        ctx.setLineDash([5, 4]);
        ctx.globalAlpha = opacity * 0.6;
        ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(pv.x, pv.y); ctx.stroke();
        ctx.restore();
      }
      break;
    }
    case "cycle_lines": {
      const stride = Math.max(1, Math.round(d.b.idx - d.a.idx));
      if (stride < 1) break;
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.setLineDash([4, 4]);
      ctx.font = "bold 9px ui-monospace, monospace";
      let idx = d.a.idx;
      const maxIters = 64;
      for (let i = 0; i < maxIters; i++) {
        const xv = xOfIdx(idx, ctxCoords.nCandles, ctxCoords.chartW);
        if (xv > ctxCoords.chartW + 20) break;
        if (xv < -20) { idx += stride; continue; }
        ctx.beginPath(); ctx.moveTo(xv, 0); ctx.lineTo(xv, chartH); ctx.stroke();
        // Cycle index label.
        ctx.save();
        ctx.setLineDash([]);
        ctx.fillStyle = "rgba(8,5,8,0.85)";
        ctx.fillRect(xv - 10, 2, 22, 13);
        ctx.fillStyle = color;
        ctx.fillText(`c${i}`, xv - 7, 12);
        ctx.restore();
        idx += stride;
      }
      ctx.setLineDash([]);
      // Stride label near anchor a.
      const A = anchorToPixel(d.a, ctxCoords);
      const txt = `Δi=${stride}`;
      const tw = ctx.measureText(txt).width + 10;
      ctx.fillStyle = "rgba(8,5,8,0.92)";
      ctx.fillRect(A.x + 4, A.y - 9, tw, 14);
      ctx.fillStyle = color;
      ctx.fillText(txt, A.x + 8, A.y + 2);
      break;
    }
    case "bars_pattern": {
      // Capture-phase preview: render a cyan dashed band over the
      // candles being selected. Once `phase === 'place'` or the
      // drawing is committed (no phase field), render the synthetic
      // candles reconstructed from `ratios` × anchor.price.
      if (d.phase === "capture") {
        const A = anchorToPixel(d.a, ctxCoords);
        const B = anchorToPixel(d.b, ctxCoords);
        const xMin = Math.min(A.x, B.x);
        const xMax = Math.max(A.x, B.x);
        ctx.save();
        ctx.globalAlpha = opacity * 0.16;
        ctx.fillStyle = "#22d3ee";
        ctx.fillRect(xMin, 0, xMax - xMin, chartH);
        ctx.restore();
        ctx.strokeStyle = "#22d3ee";
        ctx.lineWidth = 1.2;
        ctx.setLineDash([6, 4]);
        ctx.beginPath(); ctx.moveTo(xMin, 0); ctx.lineTo(xMin, chartH); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(xMax, 0); ctx.lineTo(xMax, chartH); ctx.stroke();
        ctx.setLineDash([]);
        const di = Math.abs(Math.round(d.b.idx - d.a.idx));
        const lbl = `Patrón: ${di + 1} velas`;
        ctx.font = "bold 11px ui-monospace, monospace";
        const lw = ctx.measureText(lbl).width + 12;
        ctx.fillStyle = "rgba(8,5,8,0.92)";
        ctx.fillRect(xMin + 6, 6, lw, 18);
        ctx.strokeStyle = "#22d3ee"; ctx.lineWidth = 1; ctx.strokeRect(xMin + 6, 6, lw, 18);
        ctx.fillStyle = "#22d3ee"; ctx.fillText(lbl, xMin + 12, 19);
        break;
      }
      // Committed (or place-phase) render: reconstruct candles from
      // stored OHLC ratios and call the shared drawCandle primitive.
      if (!d.ratios || !d.anchor) break;
      const yOfLocal = (p) => yOfPrice(p, ctxCoords.viewRange, chartH);
      const step = ctxCoords.chartW / Math.max(1, ctxCoords.nCandles);
      const cw = Math.max(1.5, step * 0.7);
      const anchorPrice = d.anchor.price;
      const baseIdx = d.anchor.idx;
      // Soft fill + dashed border on the bbox so it never blends with
      // real candles. We compute the bbox in pixel space first.
      const bb = barsPatternBBox(d, ctxCoords);
      if (bb) {
        ctx.save();
        ctx.globalAlpha = opacity * 0.05;
        ctx.fillStyle = "#22d3ee";
        ctx.fillRect(bb.x - 2, bb.y - 4, bb.w + 4, bb.h + 8);
        ctx.restore();
        ctx.save();
        ctx.strokeStyle = "#22d3ee";
        ctx.setLineDash([5, 4]);
        ctx.lineWidth = 1.1;
        ctx.strokeRect(bb.x - 2, bb.y - 4, bb.w + 4, bb.h + 8);
        ctx.setLineDash([]);
        ctx.restore();
        // Small "BARS" badge top-left of the bbox.
        ctx.font = "bold 9px ui-monospace, monospace";
        ctx.fillStyle = "rgba(8,5,8,0.92)";
        ctx.fillRect(bb.x - 1, bb.y - 16, 38, 13);
        ctx.fillStyle = "#22d3ee";
        ctx.fillText("BARS", bb.x + 3, bb.y - 6);
      }
      // Synthetic candles — alpha 0.6 distinguishes them from reals.
      for (let i = 0; i < d.ratios.length; i++) {
        const r = d.ratios[i];
        const cx = xOfIdx(baseIdx + i, ctxCoords.nCandles, ctxCoords.chartW);
        if (cx < -cw || cx > ctxCoords.chartW + cw) continue;
        drawCandle(ctx, cx, {
          o: r.o * anchorPrice,
          h: r.h * anchorPrice,
          l: r.l * anchorPrice,
          c: r.c * anchorPrice,
        }, yOfLocal, cw, { alpha: opacity * 0.60 });
      }
      break;
    }
    // ── B6 — Professional gap tools ─────────────────────────────────
    case "ray": {
      // Segment A→B extended infinitely past B in the P0→P1 direction.
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      const dx = B.x - A.x, dy = B.y - A.y;
      const len = Math.hypot(dx, dy);
      if (len < 0.5) break;
      // Extend to chart boundary plus a small margin so it visually
      // "exits" the chart at any angle without short ends.
      const scale = (Math.max(chartW, chartH) * 2.5) / len;
      const ex = A.x + dx * scale;
      const ey = A.y + dy * scale;
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(A.x, A.y);
      ctx.lineTo(ex, ey);
      ctx.stroke();
      // Anchor dots so the user remembers which end is finite.
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(A.x, A.y, 2.6, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(B.x, B.y, 2.0, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case "horizontal_ray": {
      // Horizontal line from A rightward only.
      const A = anchorToPixel(d.a, ctxCoords);
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(A.x, A.y);
      ctx.lineTo(chartW, A.y);
      ctx.stroke();
      // Anchor marker + price label on the left
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(A.x, A.y, 3, 0, Math.PI * 2); ctx.fill();
      ctx.font = "bold 10px ui-monospace, monospace";
      const lbl = formatPrice(d.a.price);
      const lw = ctx.measureText(lbl).width + 8;
      ctx.fillStyle = "rgba(8,5,8,0.92)";
      ctx.fillRect(A.x - lw - 4, A.y - 7, lw, 14);
      ctx.strokeStyle = color; ctx.lineWidth = 1;
      ctx.strokeRect(A.x - lw - 4, A.y - 7, lw, 14);
      ctx.fillStyle = color;
      ctx.fillText(lbl, A.x - lw + 1, A.y + 3);
      break;
    }
    case "vertical_ray": {
      // Vertical line from A upward only (y=0 at top).
      const A = anchorToPixel(d.a, ctxCoords);
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(A.x, A.y);
      ctx.lineTo(A.x, 0);
      ctx.stroke();
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(A.x, A.y, 3, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case "flag_chartist": {
      // 4 anchors: P0 pole-start, P1 pole-end, P2 upper-body, P3 lower-body.
      // Renders 3 segments: pole (P0→P1, thicker), upper (P1→P2 dashed),
      // lower (P1→P3 dashed). Pole gets a green tint so the user can
      // quickly identify it vs the consolidation body lines.
      if (!d.points || d.points.length < 2) break;
      const P = d.points.map((p) => anchorToPixel(p, ctxCoords));
      ctx.font = "bold 11px ui-sans-serif, system-ui";
      // Pole (heavier)
      ctx.save();
      ctx.strokeStyle = "#22c55e";
      ctx.lineWidth = (width || 1.5) + 0.6;
      ctx.beginPath(); ctx.moveTo(P[0].x, P[0].y); ctx.lineTo(P[1].x, P[1].y); ctx.stroke();
      ctx.restore();
      // Upper trendline
      if (P.length >= 3) {
        ctx.save();
        ctx.setLineDash([4, 3]);
        ctx.lineWidth = width;
        ctx.beginPath(); ctx.moveTo(P[1].x, P[1].y); ctx.lineTo(P[2].x, P[2].y); ctx.stroke();
        ctx.restore();
      }
      // Lower trendline
      if (P.length >= 4) {
        ctx.save();
        ctx.setLineDash([4, 3]);
        ctx.lineWidth = width;
        ctx.beginPath(); ctx.moveTo(P[1].x, P[1].y); ctx.lineTo(P[3].x, P[3].y); ctx.stroke();
        ctx.restore();
      }
      // Vertex labels: P0 P1 U L
      const labels = ["P0", "P1", "U", "L"];
      for (let i = 0; i < P.length; i++) {
        ctx.fillStyle = color;
        ctx.beginPath(); ctx.arc(P[i].x, P[i].y, 3.2, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#0a0a0c";
        const lw = ctx.measureText(labels[i] || "").width + 6;
        ctx.fillRect(P[i].x + 5, P[i].y - 9, lw, 14);
        ctx.fillStyle = color;
        ctx.fillText(labels[i] || "", P[i].x + 8, P[i].y + 2);
      }
      // Live preview ghost segment from last committed point.
      if (d.previewPt && P.length > 0 && P.length < 4) {
        const last = P[P.length - 1];
        const pv = anchorToPixel(d.previewPt, ctxCoords);
        ctx.save();
        ctx.setLineDash([5, 4]);
        ctx.globalAlpha = opacity * 0.6;
        ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(pv.x, pv.y); ctx.stroke();
        ctx.restore();
      }
      break;
    }
    case "schiff_pitchfork": {
      // Standard Schiff: median from midpoint(P0,P1) → midpoint(P1,P2);
      // rails parallel through P1 and P2. Source: TradingView docs +
      // Optuma + gocharting (verified 2025-02). Modified Schiff (origin
      // at midpoint of A and midpoint(B,C)) is intentionally NOT
      // implemented here — it would be a separate type.
      if (!d.b) break;
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      if (!d.c) {
        ctx.setLineDash([4, 4]);
        ctx.beginPath(); ctx.moveTo(A.x, A.y); ctx.lineTo(B.x, B.y); ctx.stroke();
        ctx.setLineDash([]);
        break;
      }
      const C = anchorToPixel(d.c, ctxCoords);
      const origin = { x: (A.x + B.x) / 2, y: (A.y + B.y) / 2 };
      const midBC = { x: (B.x + C.x) / 2, y: (B.y + C.y) / 2 };
      const rdx = midBC.x - origin.x, rdy = midBC.y - origin.y;
      // Channel fill between rails (subtle).
      ctx.save();
      ctx.globalAlpha = opacity * 0.06;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(B.x, B.y);
      ctx.lineTo(B.x + rdx, B.y + rdy);
      ctx.lineTo(C.x + rdx, C.y + rdy);
      ctx.lineTo(C.x, C.y);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      // Median (heavier line)
      ctx.lineWidth = (width || 1.5) + 0.4;
      ctx.beginPath();
      ctx.moveTo(origin.x, origin.y);
      ctx.lineTo(origin.x + rdx * 2.5, origin.y + rdy * 2.5);
      ctx.stroke();
      // Rails through B and C parallel to median
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(B.x, B.y); ctx.lineTo(B.x + rdx * 2.5, B.y + rdy * 2.5);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(C.x, C.y); ctx.lineTo(C.x + rdx * 2.5, C.y + rdy * 2.5);
      ctx.stroke();
      // Anchor markers + origin marker (distinct color to flag it's
      // computed, not user-clicked).
      ctx.fillStyle = color;
      for (const P of [A, B, C]) {
        ctx.beginPath(); ctx.arc(P.x, P.y, 3, 0, Math.PI * 2); ctx.fill();
      }
      ctx.save();
      ctx.fillStyle = "#facc15"; // amber — the implicit Schiff origin
      ctx.beginPath(); ctx.arc(origin.x, origin.y, 3.2, 0, Math.PI * 2); ctx.fill();
      ctx.font = "bold 9px ui-monospace, monospace";
      ctx.fillStyle = "rgba(8,5,8,0.92)";
      ctx.fillRect(origin.x + 5, origin.y - 8, 38, 13);
      ctx.fillStyle = "#facc15";
      ctx.fillText("Schiff", origin.x + 8, origin.y + 2);
      ctx.restore();
      break;
    }
    case "modified_schiff_pitchfork": {
      // Modified Schiff: origin = midpoint(P0, midpoint(P1,P2)) — i.e.
      // 50% between A and the midpoint of B→C. Direction: through
      // midpoint(B,C). Produces a flatter median than standard Schiff.
      // Source: TradingView + Optuma + facebook chartist guides (2025-02).
      if (!d.b) break;
      const A = anchorToPixel(d.a, ctxCoords);
      const B = anchorToPixel(d.b, ctxCoords);
      if (!d.c) {
        ctx.setLineDash([4, 4]);
        ctx.beginPath(); ctx.moveTo(A.x, A.y); ctx.lineTo(B.x, B.y); ctx.stroke();
        ctx.setLineDash([]);
        break;
      }
      const C = anchorToPixel(d.c, ctxCoords);
      const midBC = { x: (B.x + C.x) / 2, y: (B.y + C.y) / 2 };
      const origin = { x: (A.x + midBC.x) / 2, y: (A.y + midBC.y) / 2 };
      const rdx = midBC.x - origin.x, rdy = midBC.y - origin.y;
      ctx.save();
      ctx.globalAlpha = opacity * 0.06;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(B.x, B.y);
      ctx.lineTo(B.x + rdx, B.y + rdy);
      ctx.lineTo(C.x + rdx, C.y + rdy);
      ctx.lineTo(C.x, C.y);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      ctx.lineWidth = (width || 1.5) + 0.4;
      ctx.beginPath();
      ctx.moveTo(origin.x, origin.y);
      ctx.lineTo(origin.x + rdx * 2.5, origin.y + rdy * 2.5);
      ctx.stroke();
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(B.x, B.y); ctx.lineTo(B.x + rdx * 2.5, B.y + rdy * 2.5);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(C.x, C.y); ctx.lineTo(C.x + rdx * 2.5, C.y + rdy * 2.5);
      ctx.stroke();
      ctx.fillStyle = color;
      for (const P of [A, B, C]) {
        ctx.beginPath(); ctx.arc(P.x, P.y, 3, 0, Math.PI * 2); ctx.fill();
      }
      ctx.save();
      ctx.fillStyle = "#fb923c"; // orange — distinct from yellow Schiff
      ctx.beginPath(); ctx.arc(origin.x, origin.y, 3.2, 0, Math.PI * 2); ctx.fill();
      ctx.font = "bold 9px ui-monospace, monospace";
      ctx.fillStyle = "rgba(8,5,8,0.92)";
      ctx.fillRect(origin.x + 5, origin.y - 8, 56, 13);
      ctx.fillStyle = "#fb923c";
      ctx.fillText("Mod·Schiff", origin.x + 8, origin.y + 2);
      ctx.restore();
      break;
    }
    case "cup_handle": {
      // 5 anchors: LL (left lip), CB (cup bottom), RL (right lip),
      // HB (handle bottom), HE (handle end).
      // Render: smooth quadratic curve through LL→CB→RL for the cup,
      // then a 2-segment polyline RL→HB→HE for the handle.
      if (!d.points || d.points.length < 1) break;
      const P = d.points.map((p) => anchorToPixel(p, ctxCoords));
      ctx.font = "bold 11px ui-sans-serif, system-ui";
      // Cup curve (LL → CB control → RL): use quadraticCurveTo with CB
      // as the control point so the curve sags through CB naturally.
      if (P.length >= 3) {
        ctx.lineWidth = (width || 1.5) + 0.3;
        ctx.beginPath();
        ctx.moveTo(P[0].x, P[0].y);
        ctx.quadraticCurveTo(P[1].x, P[1].y, P[2].x, P[2].y);
        ctx.stroke();
      } else if (P.length >= 2) {
        // Live preview: straight segment until CB is committed.
        ctx.lineWidth = width;
        ctx.beginPath();
        ctx.moveTo(P[0].x, P[0].y);
        ctx.lineTo(P[1].x, P[1].y);
        ctx.stroke();
      }
      // Handle (RL → HB → HE), dashed to differentiate from the cup.
      if (P.length >= 4) {
        ctx.save();
        ctx.setLineDash([4, 3]);
        ctx.lineWidth = width;
        ctx.beginPath();
        ctx.moveTo(P[2].x, P[2].y);
        ctx.lineTo(P[3].x, P[3].y);
        if (P.length >= 5) ctx.lineTo(P[4].x, P[4].y);
        ctx.stroke();
        ctx.restore();
      }
      // Vertex labels: LL, CB, RL, HB, HE.
      const labels = ["LL", "CB", "RL", "HB", "HE"];
      for (let i = 0; i < P.length; i++) {
        ctx.fillStyle = color;
        ctx.beginPath(); ctx.arc(P[i].x, P[i].y, 3.2, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#0a0a0c";
        const lt = labels[i] || String(i + 1);
        const lw = ctx.measureText(lt).width + 6;
        ctx.fillRect(P[i].x + 5, P[i].y - 9, lw, 14);
        ctx.fillStyle = color;
        ctx.fillText(lt, P[i].x + 8, P[i].y + 2);
      }
      // Optional breakout-level horizontal ray at the cup-rim price
      // (average of LL and RL prices) — common visual cue for traders.
      if (P.length >= 5) {
        const rimY = (P[0].y + P[2].y) / 2;
        ctx.save();
        ctx.globalAlpha = opacity * 0.55;
        ctx.strokeStyle = "#22c55e";
        ctx.setLineDash([2, 4]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(P[2].x, rimY);
        ctx.lineTo(chartW, rimY);
        ctx.stroke();
        ctx.restore();
      }
      // Live preview ghost segment from last committed point.
      if (d.previewPt && P.length > 0 && P.length < 5) {
        const last = P[P.length - 1];
        const pv = anchorToPixel(d.previewPt, ctxCoords);
        ctx.save();
        ctx.setLineDash([5, 4]);
        ctx.globalAlpha = opacity * 0.6;
        ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(pv.x, pv.y); ctx.stroke();
        ctx.restore();
      }
      break;
    }
    default:
      break;
  }
  ctx.restore();
}

function formatPrice(p) {
  if (!Number.isFinite(p)) return "—";
  if (p >= 1000) return `$${p.toFixed(0)}`;
  if (p >= 1) return `$${p.toFixed(2)}`;
  return `$${p.toFixed(4)}`;
}

// ──────────────────────────────────────────────────────────────────────
//  Persistence — localStorage scoped by symbol+timeframe.
// ──────────────────────────────────────────────────────────────────────

function storageKey(symbol, timeframe) {
  return `liqmap_drawings_${symbol || "BTC"}_${timeframe || "1h"}`;
}

export function loadDrawings(symbol, timeframe) {
  try {
    const raw = localStorage.getItem(storageKey(symbol, timeframe));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

export function saveDrawings(symbol, timeframe, drawings) {
  try {
    localStorage.setItem(storageKey(symbol, timeframe), JSON.stringify(drawings || []));
  } catch { /* quota / private-mode → ignore */ }
}

// ──────────────────────────────────────────────────────────────────────
//  Tool capability helpers — used by HeatmapCanvas to decide whether a
//  pointer event begins a draft, finalizes immediately, or is ignored.
// ──────────────────────────────────────────────────────────────────────

/** Tools that drag (need pointermove between down & up). */
export function isDragTool(tool) {
  return tool === TOOLS.LINE || tool === TOOLS.RECT
      || tool === TOOLS.MEASURE
      || tool === TOOLS.FIB_RETRACEMENT
      || tool === TOOLS.FIB_EXTENSION
      || tool === TOOLS.CIRCLE
      || tool === TOOLS.ELLIPSE
      || tool === TOOLS.FREEHAND
      || tool === TOOLS.CALLOUT
      || tool === TOOLS.FIB_SPEEDFAN
      || tool === TOOLS.FIB_WEDGE
      || tool === TOOLS.GANN_FAN
      || tool === TOOLS.GANN_BOX
      // Fase B5a
      || tool === TOOLS.HIGHLIGHTER
      || tool === TOOLS.FIB_CHANNEL
      || tool === TOOLS.PRICE_RANGE
      || tool === TOOLS.DATE_RANGE
      || tool === TOOLS.DATE_PRICE_RANGE
      || tool === TOOLS.CYCLE_LINES
      // BARS_PATTERN: only the CAPTURE phase is a drag. Placement (phase 2)
      // is a single click — the caller switches behaviour using draft.phase.
      || tool === TOOLS.BARS_PATTERN
      // B6
      || tool === TOOLS.RAY;
}

/** Tools that finalize on a single pointerdown (no drag). */
export function isClickTool(tool) {
  return tool === TOOLS.HORIZONTAL || tool === TOOLS.VERTICAL
      || tool === TOOLS.TEXT || tool === TOOLS.FLAG
      || tool === TOOLS.FIB_TIMEZONE
      // B6 — 1-click directional rays
      || tool === TOOLS.HORIZONTAL_RAY || tool === TOOLS.VERTICAL_RAY;
}

/** Tool that erases on click (no drag, no draft). */
export function isEraserTool(tool) {
  return tool === TOOLS.ERASER;
}

/** True if drawing-engine should swallow the pointer event (block pan/zoom). */
export function shouldInterceptPointer(showDrawingTool, activeTool) {
  if (!showDrawingTool) return false;
  // Cursor mode passes events through to pan/zoom.
  if (activeTool === TOOLS.CURSOR) return false;
  // All other tools intercept.
  return true;
}

// ──────────────────────────────────────────────────────────────────────
//  B3b — Clone a drawing with a small offset so the duplicate is
//  visually distinguishable from the original. Offset: +5 candles in
//  idx (clamped to [0, nCandles-1]) and +1% of current price range in
//  price. Returns a new drawing with a fresh id.
// ──────────────────────────────────────────────────────────────────────
export function cloneDrawing(d, viewRange, nCandles) {
  if (!d) return null;
  const dIdx = 5;
  const dPrice = Math.max(0.01, (viewRange.max - viewRange.min) * 0.01);
  const shift = (anchor) => anchor ? ({
    idx: Math.max(0, Math.min(nCandles - 1, anchor.idx + dIdx)),
    price: anchor.price + dPrice,
  }) : anchor;
  const out = { ...d, id: makeId(), locked: false };
  if (d.a) out.a = shift(d.a);
  if (d.b) out.b = shift(d.b);
  if (d.c) out.c = shift(d.c);
  // bars_pattern stores its single position under `anchor` (not `a`);
  // include it so cloning bars_pattern places the clone next to the
  // original instead of stacking exactly on top. The OHLC `ratios[]`
  // are relative multipliers vs anchor.price and stay valid as-is.
  if (d.anchor) out.anchor = shift(d.anchor);
  if (Array.isArray(d.points)) {
    out.points = d.points.map(shift);
  }
  // Strip any in-progress fields from the source (shouldn't happen for
  // committed drawings but defensive).
  delete out.step;
  delete out.previewPt;
  return out;
}

// ──────────────────────────────────────────────────────────────────────
//  translateDrawing — move a committed drawing by a (dIdx, dPrice) delta
//  in chart-space (NOT pixels). Used by the cursor-mode drag-to-
//  reposition gesture in HeatmapCanvas: each pointermove computes a
//  fresh delta from the INITIAL pointerdown position and applies it to
//  the snapshot taken at mousedown — no cumulative drift.
//
//  Walks the same anchor tree as cloneDrawing (a, b, c, anchor, points[])
//  which covers all 7 storage patterns identified in the audit, hence
//  all 49 drawing types. The result is a NEW drawing object (immutable
//  update — safe for React state).
//
//  When `roundIdx=true` (magnet-on-release), every translated idx is
//  Math.round'ed to snap onto the nearest candle column. Price stays
//  unrounded — the user keeps free vertical placement, same semantic as
//  the existing bars_pattern reposition gesture.
// ──────────────────────────────────────────────────────────────────────
export function translateDrawing(d, dIdx, dPrice, roundIdx = false) {
  if (!d) return d;
  const shift = (a) => a ? ({
    idx: roundIdx ? Math.round(a.idx + dIdx) : a.idx + dIdx,
    price: a.price + dPrice,
  }) : a;
  const out = { ...d };
  if (d.a) out.a = shift(d.a);
  if (d.b) out.b = shift(d.b);
  if (d.c) out.c = shift(d.c);
  if (d.anchor) out.anchor = shift(d.anchor);
  if (Array.isArray(d.points)) out.points = d.points.map(shift);
  return out;
}

// Re-export the pointer-helper from chartCoords for caller ergonomics.
export { pointerToAnchor };

// ──────────────────────────────────────────────────────────────────────
//  Share permalink — serialize the current (coin, timeframe, drawings)
//  triplet into a URL-safe base64 string for #draw=<payload>. The
//  receiver hydrates the same triplet on mount. Designed for sharing a
//  full chart analysis with one link; no backend required.
//
//  Payload shape: { v:1, c:"BTC", t:"1h", d:[...drawings] }
//  Encoding:      JSON → encodeURIComponent → btoa → base64url
//  Max practical size: ~6 KB JSON (browsers handle ~32 KB URL hashes
//  comfortably). Drawings are tiny (each ~150 B) so 30+ shapes fit.
// ──────────────────────────────────────────────────────────────────────

const SHARE_VERSION = 1;

function toBase64Url(s) {
  // btoa requires ASCII; use encodeURIComponent to safely encode unicode
  // (text/callout strings) before base64'ing.
  const ascii = encodeURIComponent(s).replace(/%([0-9A-F]{2})/g,
    (_, h) => String.fromCharCode(parseInt(h, 16)));
  return btoa(ascii).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(s) {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/")
    + "===".slice((s.length + 3) % 4);
  const ascii = atob(b64);
  // Reverse the encodeURIComponent pre-pass.
  const escaped = ascii.split("").map((ch) => {
    const code = ch.charCodeAt(0);
    return code < 16 ? "%0" + code.toString(16)
         : code < 128 ? "%" + code.toString(16)
         : ch;
  }).join("");
  return decodeURIComponent(escaped);
}

/**
 * Serialize the current chart state into a URL hash payload.
 * @returns {string} URL-safe base64 string, ready to embed as `#draw=<...>`.
 */
export function serializeShare(coin, timeframe, drawings) {
  const payload = {
    v: SHARE_VERSION,
    c: String(coin || "BTC"),
    t: String(timeframe || "1h"),
    d: Array.isArray(drawings) ? drawings : [],
  };
  return toBase64Url(JSON.stringify(payload));
}

/**
 * Parse a `#draw=<payload>` hash fragment.
 * @returns {{coin:string, timeframe:string, drawings:Array}|null}
 */
export function parseShare(hash) {
  if (!hash || typeof hash !== "string") return null;
  const m = hash.match(/[#&]draw=([^&]+)/);
  if (!m) return null;
  try {
    const decoded = fromBase64Url(m[1]);
    const obj = JSON.parse(decoded);
    if (!obj || obj.v !== SHARE_VERSION) return null;
    if (typeof obj.c !== "string" || typeof obj.t !== "string") return null;
    if (!Array.isArray(obj.d)) return null;
    return { coin: obj.c, timeframe: obj.t, drawings: obj.d };
  } catch { return null; }
}


// ──────────────────────────────────────────────────────────────────────
//  Fase B5b — Bars-pattern helpers + shared candle primitive.
//  `drawCandle` is the SINGLE source of truth for one OHLC candle (used
//  both by the real candle layer in HeatmapCanvas and by the bars_pattern
//  re-render). Pixel logic mirrors the original inline loop (HeatmapCanvas
//  L358-381) so the visual output is byte-identical to the legacy code.
// ──────────────────────────────────────────────────────────────────────

/**
 * Render a single OHLC candle.
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} cx — pixel center-x.
 * @param {{o:number,h:number,l:number,c:number}} ohlc
 * @param {(p:number)=>number} yOf — price→pixel-y mapper.
 * @param {number} cw — candle body width in pixels.
 * @param {object} [opts] — { alpha?:number, upColor?:string, downColor?:string }.
 */
export function drawCandle(ctx, cx, ohlc, yOf, cw, opts = {}) {
  const isUp = ohlc.c >= ohlc.o;
  const upColor = opts.upColor || "#00d084";
  const downColor = opts.downColor || "#ff3b5c";
  const col = isUp ? upColor : downColor;
  const alpha = opts.alpha != null ? opts.alpha : 1;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = col;
  ctx.fillStyle = col;
  // Wick
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(cx, yOf(ohlc.h));
  ctx.lineTo(cx, yOf(ohlc.l));
  ctx.stroke();
  // Body
  const yO = yOf(ohlc.o);
  const yC = yOf(ohlc.c);
  const top = Math.min(yO, yC);
  const bh = Math.max(1, Math.abs(yO - yC));
  ctx.fillRect(cx - cw / 2, top, cw, bh);
  ctx.restore();
}

/**
 * Compute OHLC ratios for a captured range. Each ratio is the candle's
 * field divided by `candles[start].close` — pure multipliers, no absolute
 * prices retained, satisfying the spec's "% relativos" rule.
 * Returns null if the range is degenerate.
 */
export function captureBarsRatios(candles, startIdx, endIdx) {
  if (!Array.isArray(candles) || candles.length === 0) return null;
  const a = Math.max(0, Math.min(candles.length - 1, Math.round(Math.min(startIdx, endIdx))));
  const b = Math.max(0, Math.min(candles.length - 1, Math.round(Math.max(startIdx, endIdx))));
  if (b - a < 1) return null;
  const baseClose = candles[a]?.c;
  if (!baseClose || baseClose <= 0) return null;
  const ratios = [];
  for (let i = a; i <= b; i++) {
    const k = candles[i];
    if (!k) continue;
    ratios.push({
      o: k.o / baseClose,
      h: k.h / baseClose,
      l: k.l / baseClose,
      c: k.c / baseClose,
    });
  }
  if (ratios.length < 2) return null;
  return { ratios, span_idx: ratios.length - 1 };
}

/**
 * Bounding box of a bars_pattern drawing in PIXEL space — used for
 * (a) hit-test (point-in-rect, mirrors B3b rect hit-test) and
 * (b) drag-to-reposition (cursor-mode mousedown inside this rect).
 */
export function barsPatternBBox(d, ctxCoords) {
  if (!d || !d.ratios || !d.anchor) return null;
  const { viewRange, chartW, chartH, nCandles } = ctxCoords;
  const xMin = xOfIdx(d.anchor.idx, nCandles, chartW);
  const xMax = xOfIdx(d.anchor.idx + d.span_idx, nCandles, chartW);
  let pMin = Infinity, pMax = -Infinity;
  for (const r of d.ratios) {
    const lo = r.l * d.anchor.price;
    const hi = r.h * d.anchor.price;
    if (lo < pMin) pMin = lo;
    if (hi > pMax) pMax = hi;
  }
  const yTop = yOfPrice(pMax, viewRange, chartH);
  const yBot = yOfPrice(pMin, viewRange, chartH);
  return {
    x: Math.min(xMin, xMax),
    y: Math.min(yTop, yBot),
    w: Math.abs(xMax - xMin),
    h: Math.abs(yBot - yTop),
  };
}
